/**
 * Palantir PMS - Software para la gestiÃ³n de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creaciÃ³n / Created on: 11/10/2025
 * Todos los derechos reservados / All rights reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import * as XLSX from "xlsx";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import { ordersText } from "./ordersMessages";
import { isParStockPurchase, parStockCoverageLabel } from "../utils/purchaseRequisitionSource";
import { orderReceiptStatus, RECEIPT_STATUS_META } from "../utils/orderReceiptStatus";
import "../PurchasingUi.css";
import "./Orders.css";
import AddOrderPopup from "../components/popups/AddOrderPopup";
import AddReceivingPopup from "../components/popups/AddReceivingPopup";
import PurchaseRequisitionDetail from "../components/PurchaseRequisitionDetail";
import RequisitionProcessResult from "../components/RequisitionProcessResult";
import RequisitionTrackingPopup from "../components/popups/RequisitionTrackingPopup";
import EditOrderPopup from "../components/popups/EditOrderPopup";
import { usePOApprovalDecision } from "../components/popups/POApprovalDecisionDialog";
import useIsMobile from "../../hooks/useIsMobile";
import EditReceivingPopup from "../components/popups/EditReceivingPopup";
import EditDeliveryNotePopup from "../components/popups/EditDeliveryNotePopup";
import PriceConfirmationInbox from "../components/popups/PriceConfirmationInbox";
import { priceConfirmationText } from "../components/popups/priceConfirmationMessages";
import OcrOptionsPopup from "../components/popups/OcrOptionsPopup";
import AddDeliveryNotePopup from "../components/popups/AddDeliveryNotePopup";
import SupplierReturnsWorkspace from "../components/SupplierReturnsWorkspace";
import SupplierReturnDialog from "../components/popups/SupplierReturnDialog";
import { supplierReturnsText } from "../components/supplierReturnsMessages";
import BackordersCleanupPopup from "../components/popups/BackordersCleanupPopup";
import PurchasingDocumentDialog, { useDocumentDialog } from "../components/popups/PurchasingDocumentDialog";
import useCrossTabDataRefreshToken from "../../hooks/useCrossTabDataRefresh";
import { DEFAULT_VIEW, viewerPath } from "../components/originals/originalViewerState";
import { originalViewerText } from "../components/originals/originalViewerMessages";
import { deliveryNoteLifecycleText } from "../components/popups/deliveryNoteLifecycleMessages";
import { purchaseSupplierError, supplierMinimumMessage } from "../components/popups/documentPopupMessages";

const TAB_TITLES = {
  requisitions: "Purchase Requisitions",
  orders: "Purchase Orders",
  approvals: "Approvals",
  receivings: "Receivings",
  supplierReturns: "Supplier returns",
  deliveryNotes: "Delivery Notes",
};
const TAB_ROUTES = { requisitions: "requisitions", orders: "purchase-orders", receivings: "receivings", supplierReturns: "supplier-returns", deliveryNotes: "delivery-notes" };

const REQUISITION_STATUS_ORDER = ["draft", "confirmed", "converted", "cancelled"];
const REQUISITION_STATUS_LABELS = {
  draft: "Draft",
  confirmed: "Confirmed",
  converted: "Converted",
  cancelled: "Cancelled",
};

const SUPPLIER_DISPLAY_LIMIT = 20;

const truncateText = (value, maxLength = SUPPLIER_DISPLAY_LIMIT) => {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  if (maxLength <= 3) return text.slice(0, maxLength);
  return `${text.slice(0, maxLength - 3).trimEnd()}...`;
};

const cleanUniqueStrings = (values) =>
  Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

const splitSupplierList = (value) =>
  String(value || "")
    .split(",")
    .map((supplier) => supplier.trim())
    .filter(Boolean);

const ORDER_STATUS_ORDER = ["draft", "returned", "pending_approval", "confirmed", "sent", "acknowledged", "pending_receipt", "partial", "received", "received_with_cancellations", "completed", "receipt_cancelled", "cancelled", "rejected"];
const ORDER_STATUS_LABELS = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  returned: "Returned",
  confirmed: "Confirmed",
  sent: "Sent",
  acknowledged: "Acknowledged",
  pending_receipt: "Pending receipt",
  receipt_cancelled: "Receipt cancelled",
  partial: "Partially received",
  received: "Received",
  received_with_cancellations: "Received with cancellations",
  completed: "Completed",
  cancelled: "Cancelled",
  rejected: "Rejected",
};

const RECEIVING_STATUS_ORDER = ["draft", "pending", "received", "verified", "cancelled"];
const RECEIVING_STATUS_LABELS = {
  draft: "Draft",
  pending: "Pending",
  received: "Received",
  verified: "Verified",
  cancelled: "Cancelled",
};

const DELIVERY_NOTE_STATUS_ORDER = ["draft", "verified", "verified_with_differences", "invoiced", "closed", "cancelled"];
const DELIVERY_NOTE_STATUS_LABELS = {
  invoiced: "Invoiced",
  draft: "Draft",
  verified: "Verified",
  verified_with_differences: "Verified with differences",
  closed: "Closed",
  cancelled: "Cancelled",
};

const ORDER_STATUS_META = {
  draft: { label: "Draft", tone: "draft" },
  pending_approval: { label: "Pending Approval", tone: "pending" },
  returned: { label: "Returned", tone: "pending" },
  confirmed: { label: "Confirmed", tone: "success" },
  sent: { label: "Sent", tone: "sent" },
  acknowledged: { label: "Acknowledged", tone: "sent" },
  pending_receipt: RECEIPT_STATUS_META.pending,
  receipt_cancelled: RECEIPT_STATUS_META.cancelled,
  partial: RECEIPT_STATUS_META.partial,
  received: RECEIPT_STATUS_META.received,
  received_with_cancellations: RECEIPT_STATUS_META.received_with_cancellations,
  completed: { label: "Completed", tone: "success" },
  cancelled: { label: "Cancelled", tone: "cancelled" },
  rejected: { label: "Rejected", tone: "cancelled" },
};

const APPROVAL_MODE_LABELS = {
  any_one: "Any one",
  all: "All approvers",
};

const approvalModeLabel = (mode) => APPROVAL_MODE_LABELS[mode] || APPROVAL_MODE_LABELS.any_one;

const RECEIVING_STATUS_META = {
  draft: { label: "Draft", tone: "warning" },
  pending: { label: "Pending", tone: "info" },
  received: { label: "Received", tone: "success" },
  verified: { label: "Verified", tone: "success" },
  cancelled: { label: "Cancelled", tone: "danger" },
};

const DELIVERY_NOTE_STATUS_META = {
  invoiced: { label: "Invoiced", tone: "invoiced" },
  draft: { label: "Draft", tone: "warning" },
  verified: { label: "Verified", tone: "success" },
  verified_with_differences: { label: "Verified with differences", tone: "success" },
  closed: { label: "Closed", tone: "success" },
  cancelled: { label: "Cancelled", tone: "danger" },
};




export default function Orders({ forcedTab = null }) {
  const navigate = useNavigate();
  const { themeId, languageId } = usePmsTheme();
  const t = useCallback((key, params) => ordersText(languageId, key, params), [languageId]);
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const { confirm: confirmDocument, notify: notifyDocument, dialog } = useDocumentDialog();
  const approvalDecision = usePOApprovalDecision();
  const currentUser = readStoredUser();
  const permissions = currentUser?.permissions || {};
  const isAdminRole = Number(currentUser?.id) === 1;
  const can = (key) =>
    isAdminRole ||
    Boolean(permissions.access_settings) ||
    Boolean(permissions[key]);
  const canAct = (key) => isAdminRole || permissions[key] === true;

  const tabPermissions = {
    requisitions: can("purchasing.requisitions.view_tab"),
    orders: can("purchasing.orders.view_tab"),
    approvals: forcedTab === "approvals" && (
      can("purchasing.orders.view_tab") ||
      can("purchasing.orders.approvals.view_all")),
    receivings: can("purchasing.receivings.view_tab"),
    supplierReturns: can("purchasing.receivings.view_tab") || can("purchasing.delivery_notes.view_tab"),
    deliveryNotes: can("purchasing.delivery_notes.view_tab"),
  };

  const getDefaultTab = useCallback(() => {
    if (tabPermissions.requisitions) return "requisitions";
    if (tabPermissions.orders) return "orders";
    if (tabPermissions.receivings) return "receivings";
    if (tabPermissions.deliveryNotes) return "deliveryNotes";
    return null; // sin permisos de pestañas
  }, [
    tabPermissions.deliveryNotes,
    tabPermissions.orders,
    tabPermissions.receivings,
    tabPermissions.requisitions,
  ]);

  const [activeTab, setActiveTab] = useState(forcedTab || getDefaultTab());
  const activeTabAllowed =
    activeTab === "requisitions"
      ? tabPermissions.requisitions
      : activeTab === "orders"
      ? tabPermissions.orders
      : activeTab === "approvals"
      ? tabPermissions.approvals
      : activeTab === "receivings"
      ? tabPermissions.receivings
      : activeTab === "supplierReturns"
      ? tabPermissions.supplierReturns
      : activeTab === "deliveryNotes"
      ? tabPermissions.deliveryNotes
      : false;

  const [orders, setOrders] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [loadedListScopes, setLoadedListScopes] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupError, setCleanupError] = useState("");
  const [cleanupReview, setCleanupReview] = useState(false);
  const [processingRequisition, setProcessingRequisition] = useState(null);
  const isMobile = useIsMobile();
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [receivings, setReceivings] = useState([]);

  const businessUnitId =
    searchParams.get("business_unit_id") ||
    currentUser?.active_business_unit_id;
  const scopeRef = useRef(String(businessUnitId));
  scopeRef.current = String(businessUnitId);
  const showPriceInbox = activeTab === "deliveryNotes" && tabPermissions.deliveryNotes && searchParams.get("price_inbox") === "1";
  const setPriceInboxOpen = (open) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (open) next.set("price_inbox", "1");
      else next.delete("price_inbox");
      return next;
    }, { replace: true });
  };


  const [requisitionReviewError, setRequisitionReviewError] = useState(null);
  const [isViewRequisitionOpen, setIsViewRequisitionOpen] = useState(false);
  const [viewRequisition, setViewRequisition] = useState(null);
  const [trackingRequisition, setTrackingRequisition] = useState(null);

  // ðŸ†• Estados para ediciÃ³n de Ã³rdenes
  const [isEditOrderPopupOpen, setIsEditOrderPopupOpen] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [showCleanPopup, setShowCleanPopup] = useState(false);
  const [cleanStart, setCleanStart] = useState("");
  const [cleanEnd, setCleanEnd] = useState("");
  const [showCleanOrdersPopup, setShowCleanOrdersPopup] = useState(false);
  const [cleanOrdersStatus, setCleanOrdersStatus] = useState("draft");
  const [cleanOrdersStart, setCleanOrdersStart] = useState("");
  const [cleanOrdersEnd, setCleanOrdersEnd] = useState("");

  // ðŸ†• Estados para pestaÃ±a de Receiving
  const [isAddReceivingPopupOpen, setIsAddReceivingPopupOpen] = useState(false);
  const [isEditReceivingPopupOpen, setIsEditReceivingPopupOpen] = useState(false);
  const [editingReceivingId, setEditingReceivingId] = useState(null);

  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [isEditDeliveryNoteOpen, setIsEditDeliveryNoteOpen] = useState(false);
  const [editingDeliveryNoteId, setEditingDeliveryNoteId] = useState(null);
  const [originalNotice, setOriginalNotice] = useState("");
  const [showOcrPopup, setShowOcrPopup] = useState(false);
  const [isAddDeliveryNotePopupOpen, setIsAddDeliveryNotePopupOpen] = useState(false);
  const [isSupplierReturnOpen, setIsSupplierReturnOpen] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [orderToast, setOrderToast] = useState("");
  const [approvalRows, setApprovalRows] = useState([]);
  const [approvalSearch, setApprovalSearch] = useState("");
  const [approvalToast, setApprovalToast] = useState("");
  const [approvalActionKey, setApprovalActionKey] = useState("");
  const [canViewAllApprovals, setCanViewAllApprovals] = useState(false);
  const [requisitionSearch, setRequisitionSearch] = useState(() => searchParams.get("search") || "");
  const [requisitionStatusFilter, setRequisitionStatusFilter] = useState("all");
  const [requisitionToast, setRequisitionToast] = useState("");
  const [requisitionProcessPreviews, setRequisitionProcessPreviews] = useState({});
  const [requisitionNotesModal, setRequisitionNotesModal] = useState(null);
  const [requisitionSuppliersModal, setRequisitionSuppliersModal] = useState(null);
  const [receivingSearch, setReceivingSearch] = useState("");
  const [receivingStatusFilter, setReceivingStatusFilter] = useState("all");
  const [deliveryNoteSearch, setDeliveryNoteSearch] = useState("");
  const [deliveryNoteStatusFilter, setDeliveryNoteStatusFilter] = useState("all");
  const [deliveryNoteTypeFilter, setDeliveryNoteTypeFilter] = useState("all");

  const [showBackordersPopup, setShowBackordersPopup] = useState(false);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();
  const mountedRef = useRef(false);
  const requestSequenceRef = useRef({});
  const permissionsRef = useRef(tabPermissions);
  permissionsRef.current = tabPermissions;

  useEffect(() => {
    mountedRef.current = true;
    const requestSequences = requestSequenceRef.current;
    return () => {
      mountedRef.current = false;
      Object.keys(requestSequences).forEach((section) => {
        requestSequences[section] += 1;
      });
    };
  }, []);

  // Only the newest permitted request may update a list, including its error/loading state.
  const loadList = useCallback(async (section, allowed, url, receive) => {
    const requestedScope = String(businessUnitId);
    if (!mountedRef.current || !businessUnitId || !allowed || !permissionsRef.current[section] || scopeRef.current !== requestedScope) return;
    const sequence = (requestSequenceRef.current[section] || 0) + 1;
    requestSequenceRef.current[section] = sequence;
    const isCurrent = () => mountedRef.current
      && scopeRef.current === requestedScope
      && permissionsRef.current[section]
      && requestSequenceRef.current[section] === sequence;
    try {
      const response = await axios.get(url, {
        params: { business_unit_id: businessUnitId },
      });
      if (!isCurrent()) return;
      await receive(response, isCurrent);
      if (isCurrent()) setLoadErrors((previous) => ({ ...previous, [section]: false }));
    } catch (err) {
      if (!isCurrent()) return;
      setLoadErrors((previous) => ({ ...previous, [section]: true }));
      console.error(`[Purchasing][${section}] Error loading list:`, err);
    } finally {
      if (isCurrent()) setLoadedListScopes((previous) => ({ ...previous, [section]: requestedScope }));
    }
  }, [businessUnitId]);

  const fetchOrders = useCallback(() => loadList("orders", tabPermissions.orders, "/api/purchasing/orders", (response) => {
    setOrders(Array.isArray(response.data) ? response.data : []);
  }), [loadList, tabPermissions.orders]);

  const fetchRequisitionProcessPreviews = useCallback(async (rows, isCurrent) => {
    if (!isCurrent()) return;
    const ids = (rows || [])
      .filter((row) => !["converted", "cancelled"].includes(String(row.status || "").trim().toLowerCase()))
      .map((row) => Number(row.id))
      .filter(Boolean);

    if (!ids.length) {
      setRequisitionProcessPreviews({});
      return;
    }

    try {
      const res = await axios.post(
        "/api/purchasing/requisitions/process-preview",
        { ids },
        { skipCrossTabSync: true }
      );
      if (!isCurrent()) return;
      const previews = Array.isArray(res.data?.previews) ? res.data.previews : [];
      setRequisitionProcessPreviews(
        Object.fromEntries(previews.map((preview) => [String(preview.requisition_id), preview]))
      );
    } catch (err) {
      if (!isCurrent()) return;
      console.error("âŒ [REQUISITIONS] Error fetching process preview:", err);
      setRequisitionProcessPreviews(
        Object.fromEntries(ids.map((id) => [String(id), { requisition_id: id, mode: "process", label: "Process" }]))
      );
    }
  }, []);

  const fetchRequisitions = useCallback(() => loadList("requisitions", tabPermissions.requisitions, "/api/purchasing/requisitions", async (response, isCurrent) => {
    const rows = Array.isArray(response.data) ? response.data : [];
    setRequisitions(rows);
    await fetchRequisitionProcessPreviews(rows, isCurrent);
  }), [fetchRequisitionProcessPreviews, loadList, tabPermissions.requisitions]);

  const fetchApprovals = useCallback(() => loadList("approvals", tabPermissions.approvals, "/api/purchasing/po-approval-rules/approvals", (response) => {
    setApprovalRows(Array.isArray(response.data?.approvals) ? response.data.approvals : []);
    setCanViewAllApprovals(Boolean(response.data?.can_view_all));
  }), [loadList, tabPermissions.approvals]);

  const fetchReceivings = useCallback(() => loadList("receivings", tabPermissions.receivings, "/api/purchasing/receivings", (response) => {
    setReceivings(Array.isArray(response.data) ? response.data : []);
  }), [loadList, tabPermissions.receivings]);

  const fetchDeliveryNotes = useCallback(() => loadList("deliveryNotes", tabPermissions.deliveryNotes, "/api/purchasing/receivings/delivery-notes", (response) => {
    setDeliveryNotes(Array.isArray(response.data) ? response.data : []);
  }), [loadList, tabPermissions.deliveryNotes]);

  useEffect(() => {
    if (!orderToast) return undefined;
    const timeout = window.setTimeout(() => setOrderToast(""), 3500);
    return () => window.clearTimeout(timeout);
  }, [orderToast]);

  useEffect(() => {
    if (!requisitionToast) return undefined;
    const timeout = window.setTimeout(() => setRequisitionToast(""), 1200);
    return () => window.clearTimeout(timeout);
  }, [requisitionToast]);

  useEffect(() => {
    if (!approvalToast) return undefined;
    const timeout = window.setTimeout(() => setApprovalToast(""), 1600);
    return () => window.clearTimeout(timeout);
  }, [approvalToast]);

  const applyReceivingConfirmationResult = useCallback((result) => {
    if (result?.action !== "confirm_delivery_note") return;

    const receivingId = Number(result.receivingId || result.receiving_id);
    if (!receivingId) return;

    setReceivings((prev) =>
      (prev || []).map((receiving) =>
        Number(receiving.id) === receivingId
          ? {
              ...receiving,
              status: result.receivingStatus || "verified",
              verified_at: receiving.verified_at || new Date().toISOString(),
            }
          : receiving
      )
    );
  }, []);

  useEffect(() => {
    Object.keys(requestSequenceRef.current).forEach((section) => {
      requestSequenceRef.current[section] += 1;
    });
    setOrders([]); setRequisitions([]); setApprovalRows([]); setReceivings([]); setDeliveryNotes([]);
    setIsSupplierReturnOpen(false);
    setLoadErrors({}); setRequisitionProcessPreviews({}); setLoadedListScopes({});
  }, [businessUnitId]);

  useEffect(() => { setOriginalNotice(""); }, [businessUnitId, activeTab]);

  useEffect(() => {
    fetchOrders(); fetchRequisitions(); fetchApprovals(); fetchReceivings();
  }, [fetchOrders, fetchRequisitions, fetchApprovals, fetchReceivings, crossTabRefreshToken]);

  // Compatibility callbacks belong to this mounted workspace, not to the last render.
  useEffect(() => {
    const callbacks = { fetchOrders, fetchRequisitions, fetchApprovals };
    const previous = Object.fromEntries(Object.keys(callbacks).map((key) => [key, window[key]]));
    Object.assign(window, callbacks);
    return () => {
      Object.entries(callbacks).forEach(([key, callback]) => {
        if (window[key] !== callback) return;
        if (previous[key] === undefined) delete window[key];
        else window[key] = previous[key];
      });
    };
  }, [fetchOrders, fetchRequisitions, fetchApprovals]);

  useEffect(() => {
    if (!forcedTab) return;
    setActiveTab(forcedTab);
  }, [forcedTab]);

  useEffect(() => {
    if (forcedTab) return;
    if (activeTabAllowed) return;
    setActiveTab(getDefaultTab());
  }, [activeTabAllowed, forcedTab, getDefaultTab]);

  useEffect(() => {
    window.addEventListener("refreshReceivingsList", fetchReceivings);
    return () => window.removeEventListener("refreshReceivingsList", fetchReceivings);
  }, [fetchReceivings]);


  useEffect(() => {
    window.addEventListener("refreshOrdersList", fetchOrders);
    return () => window.removeEventListener("refreshOrdersList", fetchOrders);
  }, [fetchOrders]);

  useEffect(() => {
    window.addEventListener("refreshPoApprovalsList", fetchApprovals);
    return () => window.removeEventListener("refreshPoApprovalsList", fetchApprovals);
  }, [fetchApprovals]);

  useEffect(() => {
    if (activeTab === "deliveryNotes") {
      fetchDeliveryNotes();
    }
  }, [activeTab, fetchDeliveryNotes, crossTabRefreshToken]);

  useEffect(() => {
    window.addEventListener("refreshDeliveryNotesList", fetchDeliveryNotes);
    return () => window.removeEventListener("refreshDeliveryNotesList", fetchDeliveryNotes);
  }, [fetchDeliveryNotes]);


  const handleOpenDeliveryNote = (id) => {
    console.log(`[UI] Opening Delivery Note popup for ID=${id}`);
    setEditingDeliveryNoteId(id);
    setIsEditDeliveryNoteOpen(true);
  };

  const handleOpenDeliveryNoteOriginal = (row, event) => {
    event?.stopPropagation();
    const ocrImportId =
      row?.sourceOcrImportId ||
      row?.raw?.source_ocr_import?.id ||
      row?.raw?.sourceOcrImport?.id;

    if (!ocrImportId) return;
    const session = readStoredUser();
    if (!session?.id || String(session.id) !== String(currentUser?.id) ||
        Number(session.active_business_unit_id) !== Number(businessUnitId)) {
      setOriginalNotice("Your session or business unit changed. Reopen the original from the correct business unit.");
      return;
    }
    setOriginalNotice("");
    // A list preview owns a separate window; only document editors own a split
    // view. Keeping a page-level viewer here would overlap their modal layers.
    const path = viewerPath({ id: ocrImportId, name: row.sourceOcrFileName, businessUnitId: Number(businessUnitId) }, DEFAULT_VIEW);
    try {
      const child = window.open(path, "_blank", "popup,width=1200,height=900");
      if (child) child.opener = null;
      else setOriginalNotice("Allow pop-up windows for Pilcore and try again.");
    } catch {
      setOriginalNotice("Allow pop-up windows for Pilcore and try again.");
    }
  };



  // ðŸ§¾ Exportar a Excel segÃºn pestaÃ±a
  const exportToExcel = () => {
    const data =
      activeTab === "orders"
        ? filteredOrders.map(({ raw: o, statusLabel }) => ({
            Code: o.order_code,
            Type: o.type,
            Supplier: o.supplier?.name || "-",
            Date: o.order_date || "-",
            Expected: o.expected_delivery_date || "-",
            InitiatedBy: formatDocumentInitiator(o),
            Status: statusLabel,
            Total: o.total_amount || "-",
          }))
        : activeTab === "approvals"
        ? approvalQueueRows.map((row) => ({
            PurchaseOrder: row.orderCode,
            Supplier: row.supplierName,
            Requester: row.requesterName,
            PendingApprover: row.pendingApproverSummary,
            CurrentLevel: row.currentLevel,
            Rule: row.ruleName,
            Requisition: row.requisitionCode,
            Total: row.totalAmountLabel,
            CanApprove: row.canApprove ? "Yes" : "No",
          }))
        : activeTab === "receivings"
        ? filteredReceivings.map(({ raw: r }) => ({
            Code: r.receiving_code || "-",
            Supplier: r.supplier?.name || "-",
            DeliveryNote: r.delivery_note_number || "-",
            ReceivedDate: r.received_date || "-",
            Receiver: r.receiver_name || "-",
            InitiatedBy: formatDocumentInitiator(r),
            Status: r.status || "-",
          }))
        : activeTab === "deliveryNotes"
        ? filteredDeliveryNotes.map((dn) => ({
            Id: dn.id,
            DeliveryNote: dn.deliveryNoteNumber || "-",
            Type: dn.documentType,
            RectificationPending: dn.correctionPending ? t("Yes") : t("No"),
            Supplier: dn.supplierName || "-",
            Date: dn.deliveryDateLabel || "-",
            Status: dn.statusLabel || "-",
            Receiving: dn.receivingCode || "-",
            InitiatedBy: dn.initiatedByName || "-",
            Total: dn.totalAmountLabel || "-",
          }))
        : filteredRequisitions.map((r) => ({
            Code: r.requisitionCode,
            RequestDate: r.requestDateLabel,
            ExpectedDelivery: r.expectedDateLabel,
            RequestedBy: r.requestedBy || "-",
            Status: r.statusLabel,
            Receiving: r.receiptLabel || "-",
            Suppliers: r.suppliersText || "-",
            Lines: r.lineCount,
            Total: r.totalAmount,
            Notes: r.notes || "-",
          }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    const sheetName =
      activeTab === "orders"
        ? "PurchaseOrders"
        : activeTab === "approvals"
        ? "POApprovals"
        : activeTab === "receivings"
        ? "Receivings"
        : activeTab === "deliveryNotes"
        ? "DeliveryNotes"
        : "Requisitions";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(
      workbook,
      activeTab === "orders"
        ? "purchase_orders.xlsx"
        : activeTab === "approvals"
        ? "po_approvals.xlsx"
        : activeTab === "receivings"
        ? "receivings.xlsx"
        : activeTab === "deliveryNotes"
        ? "delivery_notes.xlsx"
        : "purchase_requisitions.xlsx"
    );
  };

  const mobileTextStyle = {};





  const normalizeStatus = useCallback((value) => String(value || "").trim().toLowerCase(), []);

  const getStatusMeta = useCallback((lookup, value) => {
    const key = normalizeStatus(value);
    const meta = (
      lookup[key] || {
        label: String(value || "-").trim() || "-",
        tone: "neutral",
      }
    );
    return { ...meta, label: t(meta.label) };
  }, [normalizeStatus, t]);

  const formatDate = useCallback((value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }
    return parsed.toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }, [locale]);

  const formatCurrency = useCallback(
    (value) =>
      Number(value || 0).toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale]
  );

  const formatCurrencyDisplay = useCallback((value, currency = "EUR") => {
    const parsed = Number.parseFloat(value);
    if (Number.isNaN(parsed)) return "-";
    return `${formatCurrency(parsed)} ${currency}`;
  }, [formatCurrency]);

  const formatUserLabel = useCallback((user) => {
    if (!user) return "-";
    return user.name || user.email || (user.id ? t("User #{id}", { id: user.id }) : "-");
  }, [t]);

  const formatDocumentInitiator = useCallback((record) => {
    if (isParStockPurchase(record)) return parStockCoverageLabel(languageId);
    const user = record?.initiated_by_user || record?.initiatedByUser || null;
    const userLabel = formatUserLabel(user);
    if (userLabel !== "-") return userLabel;
    const userId = record?.initiated_by_user_id || record?.initiatedByUserId;
    return userId ? t("User #{id}", { id: userId }) : "-";
  }, [formatUserLabel, t, languageId]);

  const approvalQueueRows = useMemo(() => {
    const query = approvalSearch.trim().toLowerCase();

    return (approvalRows || [])
      .map((approval) => {
        const pendingApprovers = Array.isArray(approval.pending_approvers)
          ? approval.pending_approvers
          : [];
        const approvalMode = pendingApprovers.some((entry) => entry.approval_mode === "any_one")
          ? "any_one"
          : pendingApprovers.some((entry) => entry.approval_mode === "all")
            ? "all"
            : "any_one";
        const pendingApproverNames = pendingApprovers
          .map((entry) => formatUserLabel(entry.user))
          .filter((name) => name && name !== "-")
          .join(approvalMode === "any_one" ? (languageId === "es" ? " o " : " or ") : ", ");
        const pendingApproverSummary = pendingApproverNames
          ? `${t(approvalModeLabel(approvalMode))}: ${pendingApproverNames}`
          : "-";
        const totalAmount = Number.parseFloat(approval.total_amount ?? 0) || 0;

        return {
          id: approval.run_id || approval.order_id,
          runId: approval.run_id,
          orderId: approval.order_id,
          orderCode: approval.order_code || `PO-${approval.order_id}`,
          supplierName: approval.supplier_name || "-",
          requesterName: isParStockPurchase(approval) ? parStockCoverageLabel(languageId) : formatUserLabel(approval.requester_user),
          createdByName: formatUserLabel(approval.created_by_user),
          pendingApproverNames: pendingApproverNames || "-",
          pendingApproverSummary,
          approvalMode,
          approvalModeLabel: approvalModeLabel(approvalMode),
          currentLevel: Number(approval.current_level || 1),
          ruleName: approval.rule_name || "-",
          requisitionCode: approval.requisition_code || "-",
          totalAmount,
          totalAmountLabel: formatCurrencyDisplay(totalAmount),
          orderDateLabel: formatDate(approval.order_date),
          expectedDateLabel: formatDate(approval.expected_delivery_date),
          status: approval.status || "pending_approval",
          approvalStatus: approval.approval_status || "pending",
          canApprove: Boolean(approval.can_approve),
          isRequester: Boolean(approval.is_requester),
          visibilityReason: approval.visibility_reason || "",
          raw: approval,
        };
      })
      .filter((row) => {
        if (!query) return true;
        return [
          row.orderCode,
          row.supplierName,
          row.requesterName,
          row.createdByName,
          row.pendingApproverNames,
          row.pendingApproverSummary,
          row.approvalModeLabel,
          row.ruleName,
          row.requisitionCode,
          row.totalAmountLabel,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
  }, [approvalRows, approvalSearch, formatCurrencyDisplay, formatDate, formatUserLabel, t, languageId]);

  const approvalSummary = useMemo(() => {
    const actionable = approvalRows.filter((row) => row.can_approve).length;
    const waitingMine = approvalRows.filter((row) => row.is_requester && !row.can_approve).length;
    const totalAmount = approvalRows.reduce(
      (sum, row) => sum + (Number.parseFloat(row.total_amount ?? 0) || 0),
      0
    );

    return {
      total: approvalRows.length,
      filtered: approvalQueueRows.length,
      actionable,
      waitingMine,
      totalAmount,
    };
  }, [approvalQueueRows.length, approvalRows]);

  const orderRows = useMemo(() => {
    return (orders || []).map((order) => {
      const documentStatus = normalizeStatus(order.status);
      const receiptStatus = orderReceiptStatus(order);
      let status = documentStatus;
      if (!["cancelled", "rejected", "returned", "pending_approval"].includes(documentStatus)) {
        if (["partial", "received", "received_with_cancellations"].includes(receiptStatus)) status = receiptStatus;
        else if (receiptStatus === "cancelled") status = "receipt_cancelled";
        else if (receiptStatus === "pending" && ["partial", "completed", "received"].includes(documentStatus)) status = "pending_receipt";
      }
      const statusMeta = getStatusMeta(ORDER_STATUS_META, status);
      const supplierName =
        order.supplier?.name ||
        order.supplier_name ||
        order.supplier?.supplier_name ||
        "-";
      const supplierEmail = order.supplier?.email || "";
      const totalAmount =
        Number.parseFloat(order.total_amount ?? order.total_price ?? order.total ?? 0) || 0;
      const notes = String(order.notes || "").trim();
      const acknowledged = Boolean(order.acknowledged_at);
      const acknowledgedDateLabel = formatDate(order.acknowledged_at);
      const acknowledgedDisplayLabel = acknowledged
        ? acknowledgedDateLabel !== "-"
          ? `${t("Yes")} · ${acknowledgedDateLabel}`
          : "Yes"
        : "No";
      const initiatedByName = formatDocumentInitiator(order);

      return {
        id: order.id,
        raw: order,
        orderCode: order.order_code || `PO-${order.id}`,
        type: order.type || "-",
        supplierName,
        supplierEmail,
        status,
        statusTone: statusMeta.tone,
        statusLabel: statusMeta.label,
        orderDateLabel: formatDate(order.order_date || order.created_at),
        expectedDateLabel: formatDate(order.expected_delivery_date),
        acknowledged,
        acknowledgedLabel: acknowledged ? "Yes" : "No",
        acknowledgedDateLabel,
        acknowledgedDisplayLabel,
        initiatedByName,
        totalAmount,
        notes,
      };
    });
  }, [formatDate, formatDocumentInitiator, getStatusMeta, normalizeStatus, orders, t]);

  const orderStatusOptions = useMemo(() => {
    const counts = orderRows.reduce((acc, row) => {
      const key = row.status || "unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const ordered = Object.keys(counts).sort((a, b) => {
      const ai = ORDER_STATUS_ORDER.indexOf(a);
      const bi = ORDER_STATUS_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return [
      { value: "all", label: "All", count: orderRows.length },
      ...ordered.map((status) => ({
        value: status,
        label:
          ORDER_STATUS_LABELS[status] ||
          (status ? status[0].toUpperCase() + status.slice(1) : "Unknown"),
        count: counts[status],
      })),
    ];
  }, [orderRows]);

  const filteredOrders = useMemo(() => {
    const query = orderSearch.trim().toLowerCase();
    const hasQuery = query.length > 0;

    return orderRows.filter((row) => {
      if (orderStatusFilter !== "all" && row.status !== orderStatusFilter) {
        return false;
      }

      if (!hasQuery) return true;

      const haystack = [
        row.orderCode,
        row.type,
        row.supplierName,
        row.statusLabel,
        row.orderDateLabel,
        row.expectedDateLabel,
        row.acknowledgedLabel,
        row.initiatedByName,
        row.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [orderRows, orderSearch, orderStatusFilter]);

  const orderSummary = useMemo(() => {
    const totalAmount = orderRows.reduce((sum, row) => sum + row.totalAmount, 0);
    const confirmed = orderRows.filter((row) => row.status === "confirmed").length;
    const draft = orderRows.filter((row) => row.status === "draft").length;
    const acknowledged = orderRows.filter((row) => row.acknowledged).length;
    const suppliers = new Set(
      orderRows.map((row) => row.supplierName).filter((name) => name && name !== "-")
    );

    return {
      total: orderRows.length,
      filtered: filteredOrders.length,
      draft,
      confirmed,
      acknowledged,
      totalAmount,
      suppliers: suppliers.size,
    };
  }, [filteredOrders.length, orderRows]);

  const receivingRows = useMemo(() => {
    return (receivings || []).map((receiving) => {
      const status = normalizeStatus(receiving.status);
      const statusTone = Object.prototype.hasOwnProperty.call(RECEIVING_STATUS_META, status)
        ? status
        : "other";
      const supplierName =
        receiving.supplier?.name ||
        receiving.supplier_name ||
        receiving.supplier?.supplier_name ||
        "-";
      const deliveryNoteNumber = String(receiving.delivery_note_number || "").trim() || "-";
      const receiverName =
        receiving.receiver_name ||
        receiving.received_by_name ||
        receiving.created_by_name ||
        receiving.created_by?.name ||
        "-";
      const initiatedByName = formatDocumentInitiator(receiving);
      const lineCount =
        Number(
          receiving.lines_count ??
            receiving.total_lines ??
            (Array.isArray(receiving.lines)
              ? receiving.lines.length
              : Array.isArray(receiving.receiving_lines)
              ? receiving.receiving_lines.length
              : 0)
        ) || 0;
      const totalAmount =
        Number.parseFloat(receiving.total_amount ?? receiving.total_price ?? receiving.total ?? 0) ||
        0;
      const hasDeliveryNote = deliveryNoteNumber !== "-";

      return {
        id: receiving.id,
        raw: receiving,
        receivingCode:
          receiving.receiving_code ||
          (hasDeliveryNote ? deliveryNoteNumber : `REC-${receiving.id}`),
        supplierName,
        deliveryNoteNumber,
        receivedDateLabel: formatDate(receiving.received_date || receiving.created_at),
        receiverName,
        initiatedByName,
        status,
        statusTone,
        statusLabel:
          RECEIVING_STATUS_LABELS[status] ||
          (status ? status[0].toUpperCase() + status.slice(1) : "Unknown"),
        lineCount,
        totalAmount,
        hasDeliveryNote,
      };
    });
  }, [formatDate, formatDocumentInitiator, normalizeStatus, receivings]);

  const receivingStatusOptions = useMemo(() => {
    const counts = receivingRows.reduce((acc, row) => {
      const key = row.status || "unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const ordered = Object.keys(counts).sort((a, b) => {
      const ai = RECEIVING_STATUS_ORDER.indexOf(a);
      const bi = RECEIVING_STATUS_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return [
      { value: "all", label: "All", count: receivingRows.length },
      ...ordered.map((status) => ({
        value: status,
        label:
          RECEIVING_STATUS_LABELS[status] ||
          (status ? status[0].toUpperCase() + status.slice(1) : "Unknown"),
        count: counts[status],
      })),
    ];
  }, [receivingRows]);

  const filteredReceivings = useMemo(() => {
    const query = receivingSearch.trim().toLowerCase();
    const hasQuery = query.length > 0;

    return receivingRows.filter((row) => {
      if (receivingStatusFilter !== "all" && row.status !== receivingStatusFilter) {
        return false;
      }

      if (!hasQuery) return true;

      const haystack = [
        row.receivingCode,
        row.statusLabel,
        row.supplierName,
        row.deliveryNoteNumber,
        row.receiverName,
        row.initiatedByName,
        row.receivedDateLabel,
        row.lineCount ? String(row.lineCount) : "",
        row.totalAmount ? formatCurrency(row.totalAmount) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [formatCurrency, receivingRows, receivingSearch, receivingStatusFilter]);

  const receivingSummary = useMemo(() => {
    const open = receivingRows.filter((row) => ["draft", "pending"].includes(row.status)).length;
    const completed = receivingRows.filter((row) =>
      ["received", "verified"].includes(row.status)
    ).length;
    const verified = receivingRows.filter((row) => row.status === "verified").length;
    const suppliers = new Set(
      receivingRows.map((row) => row.supplierName).filter((name) => name && name !== "-")
    );

    return {
      total: receivingRows.length,
      filtered: filteredReceivings.length,
      open,
      completed,
      verified,
      suppliers: suppliers.size,
      withDeliveryNote: receivingRows.filter((row) => row.hasDeliveryNote).length,
    };
  }, [filteredReceivings.length, receivingRows]);

  const deliveryNoteRows = useMemo(() => {
    return (deliveryNotes || []).map((deliveryNote) => {
      const status = normalizeStatus(deliveryNote.status);
      const statusMeta = getStatusMeta(DELIVERY_NOTE_STATUS_META, status);
      const supplierName =
        deliveryNote.supplier?.name ||
        deliveryNote.supplier_name ||
        deliveryNote.supplier?.supplier_name ||
        "-";
      const receivingCode =
        deliveryNote.receiving?.receiving_code ||
        deliveryNote.receiving_code ||
        (deliveryNote.receiving_id ? `REC-${deliveryNote.receiving_id}` : "-");
      const receiverName =
        deliveryNote.receiver_name ||
        deliveryNote.received_by_name ||
        deliveryNote.created_by_name ||
        deliveryNote.created_by?.name ||
        "-";
      const initiatedByName = formatDocumentInitiator(deliveryNote);
      const totalAmount =
        Number.parseFloat(
          deliveryNote.total_amount ?? deliveryNote.total_price ?? deliveryNote.total ?? 0
        ) * (deliveryNote.document_type === "supplier_return" ? -1 : 1) || 0;
      const lineCount =
        Number(
          deliveryNote.lines_count ??
            deliveryNote.total_lines ??
            (Array.isArray(deliveryNote.lines) ? deliveryNote.lines.length : 0)
        ) || 0;
      const sourceOcrImport = deliveryNote.source_ocr_import || deliveryNote.sourceOcrImport || null;

      return {
        id: deliveryNote.id,
        raw: deliveryNote,
        deliveryNoteNumber: deliveryNote.delivery_note_number || `DN #${deliveryNote.id}`,
        documentType: deliveryNote.document_type || "standard",
        correctionPending: status !== "cancelled" && ["pending", "partial"].includes(deliveryNote.correction_status),
        correctionStatus: deliveryNote.correction_status || "none",
        supplierName,
        receivingCode,
        receiverName,
        initiatedByName,
        status,
        statusTone: statusMeta.tone,
        statusLabel:
          DELIVERY_NOTE_STATUS_LABELS[status] ||
          (status ? status[0].toUpperCase() + status.slice(1) : "Unknown"),
        deliveryDateLabel: formatDate(deliveryNote.delivery_note_date || deliveryNote.created_at),
        totalAmount,
        currency: deliveryNote.currency || "EUR",
        totalAmountLabel: formatCurrencyDisplay(totalAmount, deliveryNote.currency || "EUR"),
        lineCount,
        sourceOcrImportId: sourceOcrImport?.id || null,
        sourceOcrFileName: sourceOcrImport?.original_file_name || "",
        hasOriginalCopy: Boolean(sourceOcrImport?.id),
        isDuplicate: Boolean(deliveryNote.is_duplicate) && status !== "cancelled",
      };
    });
  }, [deliveryNotes, formatCurrencyDisplay, formatDate, formatDocumentInitiator, getStatusMeta, normalizeStatus]);
  const duplicateDeliveryNoteCount = deliveryNoteRows.filter((row) => row.isDuplicate).length;

  const deliveryNoteStatusOptions = useMemo(() => {
    const counts = deliveryNoteRows.reduce((acc, row) => {
      const key = row.status || "unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const ordered = Object.keys(counts).sort((a, b) => {
      const ai = DELIVERY_NOTE_STATUS_ORDER.indexOf(a);
      const bi = DELIVERY_NOTE_STATUS_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return [
      { value: "all", label: "All", count: deliveryNoteRows.length },
      { value: "correction_pending", label: "Pending rectifications", count: deliveryNoteRows.filter((row) => row.correctionPending).length },
      ...ordered.map((status) => ({
        value: status,
        label:
          DELIVERY_NOTE_STATUS_LABELS[status] ||
          (status ? status[0].toUpperCase() + status.slice(1) : "Unknown"),
        count: counts[status],
      })),
    ];
  }, [deliveryNoteRows]);

  const filteredDeliveryNotes = useMemo(() => {
    const query = deliveryNoteSearch.trim().toLowerCase();
    const hasQuery = query.length > 0;

    return deliveryNoteRows.filter((row) => {
      if (deliveryNoteTypeFilter !== "all" && row.documentType !== deliveryNoteTypeFilter) return false;
      if (deliveryNoteStatusFilter === "correction_pending" ? !row.correctionPending : deliveryNoteStatusFilter !== "all" && row.status !== deliveryNoteStatusFilter) {
        return false;
      }

      if (!hasQuery) return true;

      const haystack = [
        row.deliveryNoteNumber,
        row.supplierName,
        row.receivingCode,
        row.receiverName,
        row.initiatedByName,
        row.statusLabel,
        row.deliveryDateLabel,
        row.totalAmountLabel,
        row.lineCount ? String(row.lineCount) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [deliveryNoteRows, deliveryNoteSearch, deliveryNoteStatusFilter, deliveryNoteTypeFilter]);

  const deliveryNoteSummary = useMemo(() => {
    const invoiced = deliveryNoteRows.filter((row) => row.status === "invoiced").length;
    const draft = deliveryNoteRows.filter((row) => row.status === "draft").length;
    const verified = deliveryNoteRows.filter((row) => ["verified", "verified_with_differences", "closed"].includes(row.status)).length;
    const cancelled = deliveryNoteRows.filter((row) => row.status === "cancelled").length;
    const suppliers = new Set(
      deliveryNoteRows.map((row) => row.supplierName).filter((name) => name && name !== "-")
    );
    const totalsByCurrency = deliveryNoteRows.reduce((totals, row) => {
      if (row.status !== "cancelled") totals[row.currency] = (totals[row.currency] || 0) + row.totalAmount;
      return totals;
    }, {});

    return {
      total: deliveryNoteRows.length,
      filtered: filteredDeliveryNotes.length,
      draft,
      verified,
      invoiced,
      cancelled,
      suppliers: suppliers.size,
      totalsByCurrency: Object.keys(totalsByCurrency).length ? totalsByCurrency : { EUR: 0 },
    };
  }, [deliveryNoteRows, filteredDeliveryNotes.length]);

  const requisitionRows = useMemo(() => {
    return (requisitions || []).map((r) => {
      const status = normalizeStatus(r.status);
      const receiptMeta = RECEIPT_STATUS_META[r.receipt_status];
      const processPreview = requisitionProcessPreviews[String(r.id)] || null;
      const statusTone = ["draft", "confirmed", "converted", "cancelled"].includes(status)
        ? status
        : "other";
      const requestDateRaw = r.request_date || r.created_at || null;
      const expectedDateRaw = r.expected_delivery_date || null;
      const lineCount =
        Number(
          r.lines_count ??
            r.total_lines ??
            (Array.isArray(r.lines) ? r.lines.length : 0)
        ) || 0;
      const totalAmount =
        Number.parseFloat(r.total_price ?? r.total_amount ?? r.total ?? 0) || 0;

      const suppliersFromLines = Array.isArray(r.lines)
        ? cleanUniqueStrings(
            r.lines.flatMap((line) => {
              const itemSuppliers = Array.isArray(line?.item?.item_suppliers)
                ? line.item.item_suppliers
                : [];
              const selectedSupplier = itemSuppliers.find(
                (link) =>
                  Number(link?.supplier_id || link?.supplier?.id) === Number(line?.supplier_id)
              );
              const selectedName = line?.supplier?.name || selectedSupplier?.supplier?.name;

              if (selectedName) return [selectedName];

              return itemSuppliers.map((link) => link?.supplier?.name);
            })
          )
        : [];

      const suppliersFromText = splitSupplierList(r.suppliers_list);
      const supplierNames = suppliersFromLines.length ? suppliersFromLines : suppliersFromText;
      const suppliersText = supplierNames.length
        ? supplierNames.join(", ")
        : String(r.suppliers_list || "").trim();
      const supplierCount = supplierNames.length;
      const singleSupplierName = supplierCount === 1 ? supplierNames[0] : "";
      const supplierDisplayText =
        supplierCount > 1
          ? t("{count} suppliers", { count: supplierCount })
          : singleSupplierName
          ? truncateText(singleSupplierName)
          : "-";
      const supplierTooltip = supplierNames.join("\n");

      const requestedBy = isParStockPurchase(r) ? parStockCoverageLabel(languageId) :
        r.requested_by ||
        r.requested_by_name ||
        r.created_by_name ||
        r.created_by?.name ||
        "-";

      const notes = String(r.notes || "").trim();
      const normalizedCode = r.requisition_code || `REQ-${r.id}`;
      const statusLabel =
        REQUISITION_STATUS_LABELS[status] ||
        (status ? status[0].toUpperCase() + status.slice(1) : "Unknown");

      return {
        id: r.id,
        raw: r,
        requisitionCode: normalizedCode,
        status,
        statusTone,
        statusLabel: t(statusLabel),
        receiptStatus: receiptMeta ? r.receipt_status : null,
        receiptLabel: receiptMeta ? t(receiptMeta.label) : null,
        receiptTone: receiptMeta?.tone,
        requestDateLabel: formatDate(requestDateRaw),
        expectedDateLabel: formatDate(expectedDateRaw),
        requestedBy,
        suppliersText: suppliersText || "-",
        supplierNames,
        supplierCount,
        supplierDisplayText,
        supplierTooltip,
        supplierSearchText: supplierNames.join(" "),
        lineCount,
        totalAmount,
        notes,
        processPreview,
        processActionLabel:
          status === "converted"
            ? "Converted"
            : status === "cancelled"
            ? "Cancelled"
            : processPreview?.label || "Process",
      };
    });
  }, [formatDate, normalizeStatus, requisitionProcessPreviews, requisitions, t, languageId]);

  const requisitionStatusOptions = useMemo(() => {
    const receiptCounts = requisitionRows.reduce((acc, row) => {
      if (row.receiptStatus) acc[row.receiptStatus] = (acc[row.receiptStatus] || 0) + 1;
      return acc;
    }, {});
    const counts = requisitionRows.reduce((acc, row) => {
      const key = row.status || "unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const ordered = Object.keys(counts).sort((a, b) => {
      const ai = REQUISITION_STATUS_ORDER.indexOf(a);
      const bi = REQUISITION_STATUS_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return [
      { value: "all", label: "All", count: requisitionRows.length },
      ...ordered.map((status) => ({
        value: status,
        label:
          REQUISITION_STATUS_LABELS[status] ||
          (status ? status[0].toUpperCase() + status.slice(1) : "Unknown"),
        count: counts[status],
      })),
      ...Object.entries(RECEIPT_STATUS_META)
        .filter(([status]) => receiptCounts[status])
        .map(([status, meta]) => ({ value: `receipt:${status}`, label: meta.label, count: receiptCounts[status] })),
    ];
  }, [requisitionRows]);

  const filteredRequisitions = useMemo(() => {
    const query = requisitionSearch.trim().toLowerCase();
    const hasQuery = query.length > 0;

    return requisitionRows.filter((row) => {
      const matchesStatus = requisitionStatusFilter.startsWith("receipt:")
        ? row.receiptStatus === requisitionStatusFilter.slice(8)
        : row.status === requisitionStatusFilter;
      if (requisitionStatusFilter !== "all" && !matchesStatus) {
        return false;
      }

      if (!hasQuery) return true;

      const haystack = [
        row.requisitionCode,
        row.statusLabel,
        row.receiptLabel,
        row.requestedBy,
        row.suppliersText,
        row.supplierSearchText,
        row.notes,
        row.requestDateLabel,
        row.expectedDateLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [requisitionRows, requisitionSearch, requisitionStatusFilter]);

  const requisitionSummary = useMemo(() => {
    const pending = requisitionRows.filter((row) => !["converted", "cancelled"].includes(row.status)).length;
    const converted = requisitionRows.filter((row) => row.status === "converted").length;
    const totalAmount = requisitionRows.reduce((sum, row) => sum + row.totalAmount, 0);
    const totalLines = requisitionRows.reduce((sum, row) => sum + row.lineCount, 0);
    const suppliers = new Set(
      requisitionRows
        .flatMap((row) =>
          row.supplierNames?.length ? row.supplierNames : splitSupplierList(row.suppliersText)
        )
        .map((s) => s.trim())
        .filter((s) => s && s !== "-")
    );

    return {
      total: requisitionRows.length,
      pending,
      converted,
      totalAmount,
      totalLines,
      suppliers: suppliers.size,
      filtered: filteredRequisitions.length,
    };
  }, [filteredRequisitions.length, requisitionRows]);

  const openOrder = (row) => {
    setEditingOrderId(row.id);
    setIsEditOrderPopupOpen(true);
  };

  const rows = {
    orders: filteredOrders, requisitions: filteredRequisitions, approvals: approvalQueueRows,
    receivings: filteredReceivings, deliveryNotes: filteredDeliveryNotes,
  }[activeTab] || [];
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  useEffect(() => { setPage(1); }, [activeTab, businessUnitId, pageSize, orderSearch, orderStatusFilter,
    requisitionSearch, requisitionStatusFilter, approvalSearch, receivingSearch, receivingStatusFilter,
    deliveryNoteSearch, deliveryNoteStatusFilter, deliveryNoteTypeFilter]);
  const clearFilters = () => {
    setOrderSearch(""); setRequisitionSearch(""); setApprovalSearch(""); setReceivingSearch(""); setDeliveryNoteSearch("");
    setOrderStatusFilter("all"); setRequisitionStatusFilter("all"); setReceivingStatusFilter("all"); setDeliveryNoteStatusFilter("all"); setDeliveryNoteTypeFilter("all");
  };
  const hasFilters = Boolean(orderSearch || requisitionSearch || approvalSearch || receivingSearch || deliveryNoteSearch ||
    [orderStatusFilter, requisitionStatusFilter, receivingStatusFilter, deliveryNoteStatusFilter, deliveryNoteTypeFilter].some((value) => value !== "all"));
  const pagination = (
    <div className="rq-pagination">
      <span aria-live="polite">{t("{from}–{to} of {total} results", {
        from: rows.length ? (currentPage - 1) * pageSize + 1 : 0, to: Math.min(currentPage * pageSize, rows.length), total: rows.length,
      })}</span>
      <div className="rq-paginationControls">
        {hasFilters && <button type="button" onClick={clearFilters}>{t("Clear filters")}</button>}
        <label htmlFor="purchasing-page-size">{t("Rows per page")}</label>
        <select id="purchasing-page-size" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
          {[50, 100, 200].map((size) => <option key={size} value={size}>{size}</option>)}
        </select>
        <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>{t("Previous")}</button>
        <span>{t("Page {page} of {pages}", { page: currentPage, pages: pageCount })}</span>
        <button type="button" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}>{t("Next")}</button>
      </div>
    </div>
  );

  const handleCleanup = async () => {
    if (cleanupBusy) return;
    const isOrders = showCleanOrdersPopup;
    if (!canAct(isOrders ? "purchasing.orders.clean" : "purchasing.requisitions.delete")) return;
    const start = isOrders ? cleanOrdersStart : cleanStart;
    const end = isOrders ? cleanOrdersEnd : cleanEnd;
    if (!start || !end) { setCleanupError(t("Please select both dates.")); return; }
    if (start > end) { setCleanupError(t("The end date must be on or after the start date.")); return; }
    if (!cleanupReview) { setCleanupError(""); setCleanupReview(true); return; }
    setCleanupBusy(true); setCleanupError("");
    try {
      const res = await axios.delete(isOrders
        ? `/api/purchasing/orders/clean?status=${cleanOrdersStatus}&start=${start}&end=${end}`
        : `/api/purchasing/requisitions/clean?start=${start}&end=${end}`);
      setShowCleanOrdersPopup(false); setShowCleanPopup(false); setCleanupReview(false);
      if (isOrders) await fetchOrders(); else await fetchRequisitions();
      await notifyDocument(t(isOrders ? "{count} orders deleted." : "{count} requisitions deleted.", { count: res.data.deleted }));
    } catch (err) {
      setCleanupError(t(isOrders ? "Error cleaning purchase orders." : "Error cleaning requisitions."));
    } finally { setCleanupBusy(false); }
  };

  const openApprovalOrder = (row) => {
    setEditingOrderId(row.orderId);
    setIsEditOrderPopupOpen(true);
  };

  const handleApprovalDecision = async (row, decision) => {
    if (!row?.orderId || !row.canApprove || approvalActionKey || !["approve", "reject", "return"].includes(decision)) return;
    setApprovalActionKey(`${decision}-${row.runId || row.orderId}`);
    try {
      let comments;
      if (decision === "approve") {
        if (!(await confirmDocument(t("{action} {code}?", { action: t("Approve"), code: row.orderCode }), { tone: "success" }))) return;
      } else {
        comments = await approvalDecision.ask(decision, row.orderCode);
        if (comments === null) return;
      }
      await axios.post(`/api/purchasing/po-approval-rules/orders/${row.orderId}/${decision}`, {
        approval_revision: row.raw.approval_revision ?? 0,
        ...(decision === "return" ? { comments } : decision === "reject" ? { reason: comments } : {}),
      });
      setApprovalToast(t(decision === "return" ? "{code} returned for correction." : decision === "approve" ? "{code} approved." : "{code} rejected.", { code: row.orderCode }));
      await Promise.all([fetchApprovals(), fetchOrders()]);
    } catch (err) {
      await notifyDocument(purchaseSupplierError(err, languageId) || err.response?.data?.error || t("Could not update the approval."));
      await fetchApprovals();
    } finally {
      setApprovalActionKey("");
    }
  };

  const openReceiving = (row) => {
    setEditingReceivingId(row.id);
    setIsEditReceivingPopupOpen(true);
  };

  const repeatRequisition = (requisition) => {
    if (!canAct("purchasing.requisitions.create")) return;
    navigate(`/purchasing/orders/marketplace?business_unit_id=${businessUnitId}&repeat_id=${requisition.id}`);
  };
  const openRequisition = (row) => {
    setRequisitionReviewError(null); setViewRequisition({ id: row.id }); setIsViewRequisitionOpen(true);
  };
  useEffect(() => {
    const id = searchParams.get("requisition_id");
    if (id && /^[1-9]\d*$/.test(id)) { setRequisitionReviewError(null); setViewRequisition({ id: Number(id) }); setIsViewRequisitionOpen(true); }
  }, [searchParams]);

  const openRequisitionTracking = (row) => {
    setTrackingRequisition(row);
  };

  const convertRequisition = async (row, review = null) => {
    if (!canAct("purchasing.requisitions.convert_to_orders")) return;
    if (row.status === "converted" || row.status === "cancelled" || processingRequisition) return;

    const actionLabel = row.processActionLabel || "Process";
    if (!review && !(await confirmDocument(t("{action} requisition {code}?", { action: t(actionLabel), code: row.requisitionCode }), { tone: "success" }))) {
      return;
    }

    try {
      setProcessingRequisition(row.id);
      const res = await axios.post(`/api/purchasing/requisitions/${row.id}/process`, ...(review ? [review] : []));
      if (review) { setIsViewRequisitionOpen(false); setViewRequisition(null); setRequisitionReviewError(null); }
      const createdOrders = Array.isArray(res.data?.orders)
        ? res.data.orders.length
        : Number(res.data?.created_orders || 0);
      const processedOrders = Array.isArray(res.data?.processed_orders) ? res.data.processed_orders : [];
      await notifyDocument(<RequisitionProcessResult createdCount={createdOrders} orders={processedOrders} languageId={languageId} />, {
        title: languageId === "es" ? "Resultado de la solicitud" : languageId === "ca" ? "Resultat de la sol·licitud" : "Requisition result",
      });

      await Promise.all([fetchRequisitions(), fetchOrders(), fetchApprovals()]);
    } catch (err) {
      if (err.response?.data?.code === "QUANTITY_REVIEW_REQUIRED") {
        setRequisitionReviewError(err);
        setViewRequisition(row);
        setIsViewRequisitionOpen(true);
        return;
      }
      console.error("❌ [Requisitions][Convert] Error:", err);
      await notifyDocument(purchaseSupplierError(err, languageId) || err.response?.data?.error || t("Error converting requisition. Check console for details."));
    } finally {
      setProcessingRequisition(null);
    }
  };

  const renderSupplierMinimums = (row) => row.processPreview?.orders?.filter((order) => order.minimum_order).map((order) => (
    <small className="d-block" key={order.minimum_order.supplier_id}>{supplierMinimumMessage(order.minimum_order, languageId)}</small>
  ));

  const renderRequisitionSuppliers = (row, compact = false, tooltipPlacement = "above") => {
    if (!row.supplierCount) {
      return <span className="rq-muted">-</span>;
    }

    const tooltipPlacementClass = tooltipPlacement === "below" ? "rq-hasTooltip--below" : "";

    if (row.supplierCount > 1) {
      return (
        <button
          type="button"
          className={`rq-btn rq-btn--view rq-supplierButton rq-hasTooltip ${tooltipPlacementClass} ${
            compact ? "rq-supplierButton--compact" : ""
          }`}
          onClick={() => setRequisitionSuppliersModal(row)}
          data-tooltip={row.supplierTooltip}
          aria-label={t("Show suppliers: {names}", { names: row.suppliersText })}
        >
          {row.supplierDisplayText}
        </button>
      );
    }

    const supplierName = row.supplierNames[0] || row.suppliersText;
    const visibleSupplierName = supplierName;
    const hasSupplierTooltip = supplierName.length > SUPPLIER_DISPLAY_LIMIT;

    return (
      <span
        className={`rq-pill rq-supplierChip ${hasSupplierTooltip ? `rq-hasTooltip ${tooltipPlacementClass}` : ""}`}
        data-tooltip={hasSupplierTooltip ? supplierName : undefined}
        aria-label={hasSupplierTooltip ? supplierName : undefined}
      >
        {visibleSupplierName}
      </span>
    );
  };

  const renderRequisitionNotes = (row) => {
    if (!row.notes) {
      return <span className="rq-muted">-</span>;
    }

    return (
      <button
        type="button"
        className="rq-btn rq-btn--view rq-noteViewBtn"
        onClick={() => setRequisitionNotesModal(row)}
      >{t("View")}</button>
    );
  };

  if (forcedTab && !tabPermissions[forcedTab]) {
    return (
      <div className="pilcore-purchasing-ui rq-shell" data-theme={themeId}>{t("You don't have permission to access this section.")}</div>
    );
  }

  if (!activeTab) {
    return (
      <div className="pilcore-purchasing-ui rq-shell" data-theme={themeId}>{t("You don't have permission to access this section.")}</div>
    );
  }



  if (!businessUnitId || (activeTab !== "supplierReturns" && loadedListScopes[activeTab] !== String(businessUnitId))) {
    return <div className="pilcore-purchasing-ui rq-shell" data-theme={themeId}>
      <div className="rq-loadingView"><section className="rq-loadingCard" role="status" aria-live="polite">
        <h2 className="rq-loadingTitle">{businessUnitId ? t("Loading {section}…", { section: t(TAB_TITLES[activeTab]) }) : t("No business unit selected.")}</h2>
      </section></div>
    </div>;
  }

  return (
    <div className={`rq-workspace${activeTab === "deliveryNotes" && !isMobile ? " rq-workspace--viewport" : ""}`}>
    <div className={`pilcore-purchasing-ui rq-shell${activeTab === "deliveryNotes" && !isMobile ? " rq-shell--viewport" : ""}`} data-theme={themeId}>
      <nav className="rq-processNav" aria-label={t("Purchasing process")}>
        {Object.keys(TAB_ROUTES).filter((tab) => tabPermissions[tab]).map((tab) => (
          <Link key={tab} to={`/purchasing/orders/${TAB_ROUTES[tab]}?business_unit_id=${encodeURIComponent(businessUnitId)}`}
            aria-current={activeTab === tab ? "page" : undefined} onClick={() => { if (!forcedTab) setActiveTab(tab); }}>
            {t(TAB_TITLES[tab])}
          </Link>
        ))}
      </nav>
      {originalNotice && <div className="rq-errorBanner" role="alert">{originalViewerText(languageId, originalNotice)}</div>}
      {loadErrors[activeTab] && <div className="rq-errorBanner" role="alert">
        <span>{t("Could not load this list. Your last available data is shown, if any.")}</span>
        <button type="button" className="rq-btn" onClick={() => ({ orders: fetchOrders, requisitions: fetchRequisitions,
          approvals: fetchApprovals, receivings: fetchReceivings, deliveryNotes: fetchDeliveryNotes }[activeTab]?.())}>{t("Retry loading")}</button>
      </div>}
      {/* === CONTENIDO SEGÃšN PESTAÃ‘A === */}
      {activeTab === "orders" && (
        <>

          <div className="rq-card">
            <div className="rq-head">
              <div>
                <h5 className="rq-title">{t("Purchase Orders")}</h5>
                <p className="rq-subtitle">
                  {isMobile
                    ? t("Find, open and send purchase orders quickly from your phone.")
                    : t("Track supplier commitments, acknowledgment and total purchasing volume in one place.")}
                </p>
              </div>

              <div className={`rq-controls ${isMobile ? "rq-controls--mobile" : ""}`}>
                {isMobile ? (
                  canAct("purchasing.orders.create") && <button
                    type="button"
                    className="rq-btn rq-btn--primary rq-btn--hero"
                    onClick={() => setIsAddPopupOpen(true)}
                  >{t("+ Add Order")}</button>
                ) : (
                  <div className="rq-headerActions">
                    {canAct("purchasing.orders.create") && <button
                      type="button"
                      className="rq-btn rq-btn--orderAdd"
                      onClick={() => setIsAddPopupOpen(true)}
                    >{t("+ Add Order")}</button>}
                    {(canAct("purchasing.orders.update") || canAct("purchasing.orders.clean")) && <details className="rq-moreActions"><summary>{t("More actions")}</summary><div>
                    {canAct("purchasing.orders.update") && <button
                      type="button"
                      className="rq-btn rq-btn--backorders"
                      onClick={() => setShowBackordersPopup(true)}
                    >{t("Remove Unreceived Items")}</button>}
                    {canAct("purchasing.orders.clean") && <button
                      type="button"
                      className="rq-btn rq-btn--cleanup"
                      onClick={() => { setCleanupReview(false); setCleanupError(""); setShowCleanOrdersPopup(true); }}
                    >{t("Clean Draft Orders")}</button>}
                    </div></details>}
                    <button
                      type="button"
                      className="rq-btn rq-btn--export"
                      onClick={exportToExcel}
                    >{t("Export to Excel")}</button>
                  </div>
                )}

                <input aria-label={isMobile ? t("Search orders...") : t("Search by code, supplier, initiator, status, notes...")}
                  type="search"
                  className="rq-search"
                  placeholder={isMobile ? t("Search orders...") : t("Search by code, supplier, initiator, status, notes...")}
                  value={orderSearch}
                  onChange={(event) => setOrderSearch(event.target.value)}
                />

                {<div className="rq-statusFilters">
                    {orderStatusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`rq-statusChip ${
                          orderStatusFilter === option.value ? "rq-statusChip--active" : ""
                        }`}
                        aria-pressed={orderStatusFilter === option.value}
                        onClick={() => setOrderStatusFilter(option.value)}
                      >
                        {t(option.label)}
                        <span>{option.count}</span>
                      </button>
                    ))}
                  </div>}
              </div>
            </div>

            {!isMobile && (
              <div className="rq-stats">
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Visible / Total")}</div>
                  <div className="rq-statValue">
                    {orderSummary.filtered} / {orderSummary.total}
                  </div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Draft")}</div>
                  <div className="rq-statValue">{orderSummary.draft}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Confirmed")}</div>
                  <div className="rq-statValue">{orderSummary.confirmed}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Supplier Ack")}</div>
                  <div className="rq-statValue">{orderSummary.acknowledged}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Total Amount")}</div>
                  <div className="rq-statValue">{formatCurrency(orderSummary.totalAmount)} €</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Suppliers")}</div>
                  <div className="rq-statValue">{orderSummary.suppliers}</div>
                </div>
              </div>
            )}

            {isMobile ? (
              <div className="rq-mobileList">
                {filteredOrders.length ? (
                  pagedRows.map((row) => (
                    <div key={row.id} className="rq-mobileCard">
                      <div className="rq-mobileTop">
                        <span className="rq-mobileCode">{row.orderCode}</span>
                        <span className={`rq-status rq-status--${row.statusTone}`}>{t(row.statusLabel)}</span>
                      </div>

                      <div className="rq-mobileRows">
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Supplier")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.supplierName}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Initiated By")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.initiatedByName}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Type")}</div>
                          <div className="rq-mobileValue">{t(row.type)}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Order Date")}</div>
                          <div className="rq-mobileValue">{row.orderDateLabel}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Expected")}</div>
                          <div className="rq-mobileValue">{row.expectedDateLabel}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Supplier Ack")}</div>
                          <div className="rq-mobileValue">
                            <span className={`rq-pill ${row.acknowledged ? "rq-pill--success" : ""}`}>
                              {t(row.acknowledgedLabel)}
                            </span>
                          </div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Total")}</div>
                          <div className="rq-mobileValue">{formatCurrency(row.totalAmount)} EUR</div>
                        </div>
                      </div>

                      <div className="rq-mobileActions">
                        <button
                          type="button"
                          className="rq-btn rq-btn--view"
                          onClick={() => openOrder(row)}
                        >{t("View / Edit")}</button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rq-empty">{t("No purchase orders match your search.")}</div>
                )}
              </div>
            ) : (
              <div className="rq-tableWrap">
                <table className="rq-table rq-table--orders">
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "6%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t("Order Code")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Type")}</th>
                      <th>{t("Supplier")}</th>
                      <th>{t("Initiated By")}</th>
                      <th>{t("Order Date")}</th>
                      <th>{t("Expected")}</th>
                      <th>{t("Supplier Ack")}</th>
                      <th>{t("Total (EUR)")}</th>
                      <th>{t("Notes")}</th>
                      <th>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length ? (
                      pagedRows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <button
                              type="button"
                              className="rq-codeBtn"
                              onClick={() => openOrder(row)}
                            >
                              {row.orderCode}
                            </button>
                          </td>
                          <td>
                            <span className={`rq-status rq-status--${row.statusTone}`}>{t(row.statusLabel)}</span>
                          </td>
                          <td>{t(row.type)}</td>
                          <td>
                            <span className="rq-pill">{row.supplierName}</span>
                          </td>
                          <td>{row.initiatedByName || <span className="rq-muted">-</span>}</td>
                          <td>{row.orderDateLabel}</td>
                          <td>{row.expectedDateLabel}</td>
                          <td>
                            <span className={`rq-pill ${row.acknowledged ? "rq-pill--success" : ""}`}>
                              {row.acknowledged
                                ? row.acknowledgedDateLabel !== "-"
                                  ? `${t("Yes")} · ${row.acknowledgedDateLabel}`
                                  : t("Yes")
                                : t("No")}
                            </span>
                          </td>
                          <td>{formatCurrency(row.totalAmount)}</td>
                          <td className="rq-notes rq-notes--tight" title={row.notes || ""}>
                            {row.notes || <span className="rq-muted">-</span>}
                          </td>
                          <td className="rq-cell--actions">
                            <div className="rq-actions">
                              <button
                                type="button"
                                className="rq-btn rq-btn--view"
                                onClick={() => openOrder(row)}
                              >{t("View / Edit")}</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="rq-empty">{t("No purchase orders match the current filters.")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          {pagination}
</div>
        </>
      )}

      {activeTab === "approvals" ? (

        <>

          <div className="rq-card">
            <div className="rq-head">
              <div>
                <h5 className="rq-title">{t("Approvals")}</h5>
                <p className="rq-subtitle">
                  {canViewAllApprovals
                    ? t("Pending purchase order approvals across the active business unit.")
                    : t("Purchase orders waiting for your approval or pending from your requisitions.")}
                </p>
              </div>

              <div className={`rq-controls ${isMobile ? "rq-controls--mobile" : ""}`}>
                {!isMobile && (
                  <div className="rq-headerActions">
                    <button
                      type="button"
                      className="rq-btn rq-btn--export"
                      onClick={exportToExcel}
                    >{t("Export to Excel")}</button>
                  </div>
                )}

                <input aria-label={
                    isMobile
                      ? t("Search approvals...")
                      : t("Search by PO, supplier, requester, approver, rule...")
                  }
                  type="search"
                  className="rq-search"
                  placeholder={
                    isMobile
                      ? t("Search approvals...")
                      : t("Search by PO, supplier, requester, approver, rule...")
                  }
                  value={approvalSearch}
                  onChange={(event) => setApprovalSearch(event.target.value)}
                />
              </div>
            </div>

            {approvalToast && <div className="rq-toast">{approvalToast}</div>}

            {!isMobile && (
              <div className="rq-stats">
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Visible / Total")}</div>
                  <div className="rq-statValue">
                    {approvalSummary.filtered} / {approvalSummary.total}
                  </div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Can Approve")}</div>
                  <div className="rq-statValue">{approvalSummary.actionable}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("My Requests Waiting")}</div>
                  <div className="rq-statValue">{approvalSummary.waitingMine}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Scope")}</div>
                  <div className="rq-statValue">{canViewAllApprovals ? t("All") : t("Mine")}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Total Amount")}</div>
                  <div className="rq-statValue">{formatCurrency(approvalSummary.totalAmount)} €</div>
                </div>
              </div>
            )}

            {isMobile ? (
              <div className="rq-mobileList">
                {approvalQueueRows.length ? (
                  pagedRows.map((row) => (
                    <div key={row.id} className="rq-mobileCard" style={mobileTextStyle}>
                      <div className="rq-mobileTop">
                        <span className="rq-mobileCode">{row.orderCode}</span>
                        <span className="rq-status rq-status--pending">{t("Pending")}</span>
                      </div>

                      <div className="rq-mobileRows">
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Supplier")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.supplierName}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Requester")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.requesterName}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Pending With")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.pendingApproverSummary}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Level")}</div>
                          <div className="rq-mobileValue">{row.currentLevel}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Total")}</div>
                          <div className="rq-mobileValue">{row.totalAmountLabel}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Rule")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.ruleName}</div>
                        </div>
                      </div>

                      <div className="rq-mobileActions">
                        <button
                          type="button"
                          className="rq-btn rq-btn--view"
                          onClick={() => openApprovalOrder(row)}
                        >{t("View PO")}</button>
                        {row.canApprove ? (
                          <>
                            <button
                              type="button"
                              className="rq-btn rq-btn--action"
                              disabled={Boolean(approvalActionKey)}
                              onClick={() => handleApprovalDecision(row, "approve")}
                            >{t("Approve")}</button>
                            <button type="button" className="rq-btn rq-btn--cleanup" disabled={Boolean(approvalActionKey)}
                              onClick={() => handleApprovalDecision(row, "return")}>{t("Return with Comments")}</button>
                            <button
                              type="button"
                              className="rq-btn rq-btn--cleanup"
                              disabled={Boolean(approvalActionKey)}
                              onClick={() => handleApprovalDecision(row, "reject")}
                            >{t("Reject")}</button>
                          </>
                        ) : (
                          <button type="button" className="rq-btn" disabled>{t("Waiting")}</button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rq-empty">{t("No pending approvals match your search.")}</div>
                )}
              </div>
            ) : (
              <div className="rq-tableWrap">
                <table className="rq-table">
                  <thead>
                    <tr>
                      <th>{t("Purchase Order")}</th>
                      <th>{t("Supplier")}</th>
                      <th>{t("Total")}</th>
                      <th>{t("Requester")}</th>
                      <th>{t("Pending With")}</th>
                      <th>{t("Level")}</th>
                      <th>{t("Rule")}</th>
                      <th>{t("Requisition")}</th>
                      <th className="rq-cell--actions">{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalQueueRows.length ? (
                      pagedRows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <button
                              type="button"
                              className="rq-codeBtn"
                              onClick={() => openApprovalOrder(row)}
                            >
                              {row.orderCode}
                            </button>
                          </td>
                          <td>
                            <span className="rq-pill">{row.supplierName}</span>
                          </td>
                          <td>{row.totalAmountLabel}</td>
                          <td>{row.requesterName}</td>
                          <td>
                            <span className="rq-pill rq-pill--multiline">
                              {row.pendingApproverSummary}
                            </span>
                          </td>
                          <td>
                            <span className="rq-pill">{row.currentLevel}</span>
                          </td>
                          <td className="rq-notes rq-notes--tight" title={row.ruleName}>
                            {row.ruleName}
                          </td>
                          <td>{row.requisitionCode}</td>
                          <td className="rq-cell--actions">
                            <div className="rq-actions">
                              <button
                                type="button"
                                className="rq-btn rq-btn--view"
                                onClick={() => openApprovalOrder(row)}
                              >{t("View PO")}</button>
                              {row.canApprove ? (
                                <>
                                  <button
                                    type="button"
                                    className="rq-btn rq-btn--action"
                                    disabled={Boolean(approvalActionKey)}
                                    onClick={() => handleApprovalDecision(row, "approve")}
                                  >{t("Approve")}</button>
                                  <button type="button" className="rq-btn rq-btn--cleanup" disabled={Boolean(approvalActionKey)}
                                    onClick={() => handleApprovalDecision(row, "return")}>{t("Return with Comments")}</button>
                                  <button
                                    type="button"
                                    className="rq-btn rq-btn--cleanup"
                                    disabled={Boolean(approvalActionKey)}
                                    onClick={() => handleApprovalDecision(row, "reject")}
                                  >{t("Reject")}</button>
                                </>
                              ) : (
                                <button type="button" className="rq-btn" disabled>{t("Waiting")}</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="rq-empty">{t("No pending approvals match the current filters.")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          {pagination}
</div>
        </>
        ) : activeTab === "requisitions" ? (

        <>

          <div className="rq-card">
            <div className="rq-head">
              <div>
                <h5 className="rq-title">{t("Purchase Requisitions")}</h5>
                <p className="rq-subtitle">
                  {isMobile
                    ? t("Create and reopen requisitions quickly from your phone.")
                    : t("Track internal demand, supplier load and conversion readiness in one place.")}
                </p>
              </div>

              <div className={`rq-controls ${isMobile ? "rq-controls--mobile" : ""}`}>
                {isMobile ? (
                  canAct("purchasing.requisitions.create") && <button
                    type="button"
                    className="rq-btn rq-btn--primary rq-btn--hero"
                    onClick={() => navigate(`/purchasing/orders/marketplace?business_unit_id=${encodeURIComponent(businessUnitId)}`)}
                  >{t("+ Add Requisition")}</button>
                ) : (
                  <div className="rq-headerActions">
                    {canAct("purchasing.requisitions.create") && <button
                      type="button"
                      className="rq-btn rq-btn--view"
                      onClick={() => navigate(`/purchasing/orders/marketplace?business_unit_id=${encodeURIComponent(businessUnitId)}`)}
                    >{t("+ Add Requisition")}</button>}
                    {canAct("purchasing.requisitions.delete") && <details className="rq-moreActions"><summary>{t("More actions")}</summary><div>
                    <button
                      type="button"
                      className="rq-btn rq-btn--cleanup"
                      onClick={() => { setCleanupReview(false); setCleanupError(""); setShowCleanPopup(true); }}
                    >{t("Clean")}</button>
                    </div></details>}
                    <button
                      type="button"
                      className="rq-btn"
                      onClick={exportToExcel}
                    >{t("Export to Excel")}</button>
                  </div>
                )}
                <input aria-label={isMobile ? t("Search requisitions...") : t("Search by code, requester, supplier, notes...")}
                  type="text"
                  className="rq-search"
                  placeholder={isMobile ? t("Search requisitions...") : t("Search by code, requester, supplier, notes...")}
                  value={requisitionSearch}
                  onChange={(e) => setRequisitionSearch(e.target.value)}
                />
                {<div className="rq-statusFilters">
                    {requisitionStatusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`rq-statusChip ${
                          requisitionStatusFilter === option.value ? "rq-statusChip--active" : ""
                        }`}
                        aria-pressed={requisitionStatusFilter === option.value}
                        onClick={() => setRequisitionStatusFilter(option.value)}
                      >
                        {t(option.label)}
                        <span>{option.count}</span>
                      </button>
                    ))}
                  </div>}
              </div>
            </div>

            {requisitionToast && <div className="rq-toast">{requisitionToast}</div>}

            {!isMobile && (
            <div className="rq-stats">
              <div className="rq-stat">
                <div className="rq-statLabel">{t("Visible / Total")}</div>
                <div className="rq-statValue">
                  {requisitionSummary.filtered} / {requisitionSummary.total}
                </div>
              </div>
              <div className="rq-stat">
                <div className="rq-statLabel">{t("Pending Conversion")}</div>
                <div className="rq-statValue">{requisitionSummary.pending}</div>
              </div>
              <div className="rq-stat">
                <div className="rq-statLabel">{t("Converted")}</div>
                <div className="rq-statValue">{requisitionSummary.converted}</div>
              </div>
              <div className="rq-stat">
                <div className="rq-statLabel">{t("Total Requested")}</div>
                <div className="rq-statValue">{formatCurrency(requisitionSummary.totalAmount)} €</div>
              </div>
              <div className="rq-stat">
                <div className="rq-statLabel">{t("Lines / Suppliers")}</div>
                <div className="rq-statValue">
                  {requisitionSummary.totalLines} / {requisitionSummary.suppliers}
                </div>
              </div>
            </div>
            )}

            {isMobile ? (
              <div className="rq-mobileList">
                {filteredRequisitions.length ? (
                  pagedRows.map((row, index) => (
                    <div key={row.id} className="rq-mobileCard" style={mobileTextStyle}>
                      <div className="rq-mobileTop">
                        <span className="rq-mobileCode">{row.requisitionCode}</span>
                        <div className="rq-statusStack">
                          <span className={`rq-status rq-status--${row.statusTone}`}>{t(row.statusLabel)}</span>
                          {row.receiptLabel && <span className={`rq-status rq-status--${row.receiptTone}`} title={t("Receiving")}>{row.receiptLabel}</span>}
                        </div>
                      </div>

                      <div className="rq-mobileRows">
                        {isParStockPurchase(row.raw) && <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Requested By")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.requestedBy}</div>
                        </div>}
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Expected")}</div>
                          <div className="rq-mobileValue">{row.expectedDateLabel}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Suppliers")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">
                            {renderRequisitionSuppliers(row, true, index === 0 ? "below" : "above")}
                            {renderSupplierMinimums(row)}
                          </div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Lines / Total")}</div>
                          <div className="rq-mobileValue">
                            {row.lineCount} / {formatCurrency(row.totalAmount)} €
                          </div>
                        </div>
                      </div>

                      {row.notes && (
                        <div className="rq-mobileField mt-2">
                          <div className="rq-mobileLabel">{t("Notes")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">
                            {renderRequisitionNotes(row)}
                          </div>
                        </div>
                      )}

                      <div className="rq-mobileActions">
                        <button
                          type="button"
                          className="rq-btn rq-btn--primary rq-btn--hero"
                          onClick={() => openRequisition(row)}
                        >{t("View / Edit")}</button>
                        {row.lineCount > 0 && (
                          <button
                            type="button"
                            className="rq-btn rq-btn--track"
                            onClick={() => openRequisitionTracking(row)}
                          >{t("Track")}</button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rq-empty">{t("No requisitions match your search.")}</div>
                )}
              </div>
            ) : (
              <div className="rq-tableWrap">
                <table className="rq-table rq-table--requisitions">
                  <colgroup>
                    <col className="rq-col-code" />
                    <col className="rq-col-status" />
                    <col className="rq-col-date" />
                    <col className="rq-col-expected" />
                    <col className="rq-col-requester" />
                    <col className="rq-col-suppliers" />
                    <col className="rq-col-lines" />
                    <col className="rq-col-total" />
                    <col className="rq-col-notes" />
                    <col className="rq-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t("Code")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Request Date")}</th>
                      <th>{t("Expected")}</th>
                      <th>{t("Requested By")}</th>
                      <th className="rq-cell--suppliers">{t("Suppliers")}</th>
                      <th>{t("Lines")}</th>
                      <th>{t("Total (€)")}</th>
                      <th className="rq-cell--notes">{t("Notes")}</th>
                      <th className="rq-cell--actions">{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequisitions.length ? (
                      pagedRows.map((row, index) => (
                        <tr key={row.id}>
                          <td>
                            <button
                              type="button"
                              className="rq-codeBtn"
                              onClick={() => openRequisition(row)}
                            >
                              {row.requisitionCode}
                            </button>
                          </td>
                          <td>
                            <div className="rq-statusStack">
                              <span className={`rq-status rq-status--${row.statusTone}`}>{t(row.statusLabel)}</span>
                              {row.receiptLabel && <span className={`rq-status rq-status--${row.receiptTone}`} title={t("Receiving")}>{row.receiptLabel}</span>}
                            </div>
                          </td>
                          <td>{row.requestDateLabel}</td>
                          <td>{row.expectedDateLabel}</td>
                          <td>{row.requestedBy || <span className="rq-muted">-</span>}</td>
                          <td className="rq-cell--suppliers">
                            <div className="rq-supplierCell">
                              {renderRequisitionSuppliers(row, false, index === 0 ? "below" : "above")}
                            </div>
                            {renderSupplierMinimums(row)}
                          </td>
                          <td>
                            <span className="rq-pill">{row.lineCount}</span>
                          </td>
                          <td>{formatCurrency(row.totalAmount)}</td>
                          <td className="rq-cell--notes">{renderRequisitionNotes(row)}</td>
                          <td className="rq-cell--actions">
                            <div className="rq-actions">
                              <button
                                type="button"
                                className="rq-btn rq-btn--view"
                                onClick={() => openRequisition(row)}
                              >{t("View / Edit")}</button>
                              {row.lineCount > 0 && (
                                <>
                                  <button
                                    type="button"
                                    className="rq-btn rq-btn--track"
                                    onClick={() => openRequisitionTracking(row)}
                                  >{t("Track")}</button>
                                  {canAct("purchasing.requisitions.convert_to_orders") && <button
                                    type="button"
                                    className="rq-btn rq-btn--action"
                                    disabled={Boolean(processingRequisition) || row.status === "converted" || row.status === "cancelled"}
                                    onClick={() => convertRequisition(row)}
                                  >
                                    {t(row.processActionLabel)}
                                  </button>}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="rq-empty">{t("No requisitions match the current filters.")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          {pagination}
</div>

                </>
      ) : activeTab === "supplierReturns" ? (
        <SupplierReturnsWorkspace key={String(businessUnitId)} businessUnitId={businessUnitId} onViewDeliveryNote={tabPermissions.deliveryNotes ? handleOpenDeliveryNote : undefined} />
      ) : activeTab === "receivings" ? (
        <>
          {/* === NUEVA PESTAÃ‘A: RECEIVINGS === */}


          <div className="rq-card">
            <div className="rq-head">
              <div>
                <h5 className="rq-title">{t("Receivings")}</h5>
                <p className="rq-subtitle">
                  {isMobile
                    ? t("Register and reopen receivings quickly from your phone.")
                    : t("Track inbound goods, delivery note capture and receiving status in one place.")}
                </p>
              </div>

              <div className={`rq-controls ${isMobile ? "rq-controls--mobile" : ""}`}>
                {isMobile ? (
                  canAct("purchasing.receivings.create") && <button
                    type="button"
                    className="rq-btn rq-btn--primary rq-btn--hero"
                    onClick={() => setIsAddReceivingPopupOpen(true)}
                  >{t("+ Add Receiving")}</button>
                ) : (
                  <div className="rq-headerActions">
                    {canAct("purchasing.receivings.create") && <button
                      type="button"
                      className="rq-btn rq-btn--view"
                      onClick={() => setIsAddReceivingPopupOpen(true)}
                    >{t("+ Add Receiving")}</button>}
                    <button
                      type="button"
                      className="rq-btn"
                      onClick={exportToExcel}
                    >{t("Export to Excel")}</button>
                  </div>
                )}
                <input aria-label={
                    isMobile
                      ? t("Search receivings...")
                      : t("Search by code, supplier, delivery note, receiver, initiator...")
                  }
                  type="search"
                  className="rq-search"
                  placeholder={
                    isMobile
                      ? t("Search receivings...")
                      : t("Search by code, supplier, delivery note, receiver, initiator...")
                  }
                  value={receivingSearch}
                  onChange={(event) => setReceivingSearch(event.target.value)}
                />

                {<div className="rq-statusFilters">
                    {receivingStatusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`rq-statusChip ${
                          receivingStatusFilter === option.value ? "rq-statusChip--active" : ""
                        }`}
                        aria-pressed={receivingStatusFilter === option.value}
                        onClick={() => setReceivingStatusFilter(option.value)}
                      >
                        {t(option.label)}
                        <span>{option.count}</span>
                      </button>
                    ))}
                  </div>}
              </div>
            </div>

            {!isMobile && (
              <div className="rq-stats">
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Visible / Total")}</div>
                  <div className="rq-statValue">
                    {receivingSummary.filtered} / {receivingSummary.total}
                  </div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Open")}</div>
                  <div className="rq-statValue">{receivingSummary.open}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Completed")}</div>
                  <div className="rq-statValue">{receivingSummary.completed}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Verified")}</div>
                  <div className="rq-statValue">{receivingSummary.verified}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Suppliers")}</div>
                  <div className="rq-statValue">{receivingSummary.suppliers}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("With Delivery Note")}</div>
                  <div className="rq-statValue">{receivingSummary.withDeliveryNote}</div>
                </div>
              </div>
            )}

            {isMobile ? (
              <div className="rq-mobileList">
                {filteredReceivings.length ? (
                  pagedRows.map((row) => (
                    <div key={row.id} className="rq-mobileCard" style={mobileTextStyle}>
                      <div className="rq-mobileTop">
                        <span className="rq-mobileCode">{row.receivingCode}</span>
                        <span className={`rq-status rq-status--${row.statusTone}`}>
                          {t(row.statusLabel)}
                        </span>
                      </div>

                      <div className="rq-mobileRows">
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Supplier")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">
                            {row.supplierName}
                          </div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Delivery Note")}</div>
                          <div className="rq-mobileValue">{row.deliveryNoteNumber}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Received Date")}</div>
                          <div className="rq-mobileValue">{row.receivedDateLabel}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Receiver")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">
                            {row.receiverName}
                          </div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Initiated By")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">
                            {row.initiatedByName}
                          </div>
                        </div>
                      </div>

                      <div className="rq-mobileActions">
                        <button
                          type="button"
                          className="rq-btn rq-btn--primary rq-btn--hero"
                          onClick={() => openReceiving(row)}
                        >{t("View / Edit")}</button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rq-empty">{t("No receivings match your search.")}</div>
                )}
              </div>
            ) : (
              <div className="rq-tableWrap">
                <table className="rq-table">
                  <thead>
                    <tr>
                      <th>{t("Receiving Code")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Supplier")}</th>
                      <th>{t("Delivery Note")}</th>
                      <th>{t("Received Date")}</th>
                      <th>{t("Receiver")}</th>
                      <th>{t("Initiated By")}</th>
                      <th style={{ textAlign: "right" }}>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReceivings.length ? (
                      pagedRows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <button
                              type="button"
                              className="rq-codeBtn"
                              onClick={() => openReceiving(row)}
                            >
                              {row.receivingCode}
                            </button>
                          </td>
                          <td>
                            <span className={`rq-status rq-status--${row.statusTone}`}>
                              {t(row.statusLabel)}
                            </span>
                          </td>
                          <td>
                            <span className="rq-pill">{row.supplierName}</span>
                          </td>
                          <td>{row.deliveryNoteNumber}</td>
                          <td>{row.receivedDateLabel}</td>
                          <td>{row.receiverName || <span className="rq-muted">-</span>}</td>
                          <td>{row.initiatedByName || <span className="rq-muted">-</span>}</td>
                          <td>
                            <div className="rq-actions">
                              <button
                                type="button"
                                className="rq-btn rq-btn--view"
                                onClick={() => openReceiving(row)}
                              >{t("View / Edit")}</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="rq-empty">{t("No receivings match the current filters.")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          {pagination}
</div>
        </>
      ) : false && activeTab === "deliveryNotes" ? (
        <>
          {/* === NUEVA PESTAÃ‘A: DELIVERY NOTES === */}
          {isMobile ? (
            <div className="po-mobileList">
              {deliveryNotes.length ? (
                deliveryNotes.map((dn) => {
                  const deliveryStatus = getStatusMeta(DELIVERY_NOTE_STATUS_META, dn.status);
                  return true ? (
                    <div
                      key={dn.id}
                      className="po-mobileCard po-mobileCard--delivery"
                      style={mobileTextStyle}
                    >
                      <div className="po-mobileCardHead">
                        <span className="po-mobileCardCode">
                          {dn.delivery_note_number || `DN #${dn.id}`}
                        </span>
                        <span className={`po-mobileStatus po-mobileStatus--${deliveryStatus.tone}`}>
                          {deliveryStatus.label}
                        </span>
                      </div>
                      <div className="po-mobileGrid">
                        <div className="po-mobileField">
                          <div className="po-mobileLabel">{t("Supplier")}</div>
                          <div className="po-mobileValue">{dn.supplier?.name || "-"}</div>
                        </div>
                        <div className="po-mobileField">
                          <div className="po-mobileLabel">{t("Date")}</div>
                          <div className="po-mobileValue">{formatDate(dn.delivery_note_date)}</div>
                        </div>
                        <div className="po-mobileField">
                          <div className="po-mobileLabel">{t("Status")}</div>
                          <div className="po-mobileValue">{deliveryStatus.label}</div>
                        </div>
                        <div className="po-mobileField">
                          <div className="po-mobileLabel">{t("Total")}</div>
                          <div className="po-mobileValue">{formatCurrencyDisplay(dn.total_amount)}</div>
                        </div>
                      </div>
                      <div className="po-mobileFooter">
                        <button
                          type="button"
                          className="po-mobileActionBtn po-mobileActionBtn--primary"
                          onClick={() => handleOpenDeliveryNote(dn.id)}
                        >{t("View / Edit")}</button>
                      </div>
                    </div>
                  ) : null; /*
                  <div
                    key={dn.id}
                    className="p-3 rounded-3 shadow-sm"
                    style={{
                      backgroundColor: "#1a1a1a",
                      border: "1px solid #ffc107",
                      color: "#fff",
                      ...mobileTextStyle,
                    }}
                    onClick={() => handleOpenDeliveryNote(dn.id)}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-bold text-warning">
                        {dn.delivery_note_number || `DN #${dn.id}`}
                      </span>
                      <span>
                        {dn.status === "draft"
                          ? "ðŸŸ¡ Draft"
                          : dn.status === "verified"
                          ? "ðŸŸ¢ Verified"
                          : dn.status === "cancelled"
                          ? "âŒ Cancelled"
                          : dn.status}
                      </span>
                    </div>
                    <div className="small">
                      <div>Supplier: {dn.supplier?.name || "-"}</div>
                      <div>Date: {dn.delivery_note_date || "-"}</div>
                      <div>Total: â‚¬{dn.total_amount || "-"}</div>
                    </div>
                  </div>
                  */
                })
              ) : (
                <div className="po-mobileEmpty">{t("No delivery notes found.")}</div>
              )}
            </div>
          ) : (
            <div className="table-container">
              <h5 className="text-warning fw-bold mb-3">{t("ðŸ“¦ Delivery Notes")}</h5>
              <table className="table table-dark table-bordered table-hover align-middle text-center">
                <thead className="bg-secondary text-white">
                  <tr>
                    <th>ID</th>
                    <th>{t("Delivery Note #")}</th>
                    <th>{t("Supplier")}</th>
                    <th>{t("Date")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Total (â‚¬)")}</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryNotes.length > 0 ? (
                    deliveryNotes.map((dn) => (
                      <tr key={dn.id}>
                        <td>{dn.id}</td>
                        <td
                          className="text-warning fw-bold"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleOpenDeliveryNote(dn.id)}
                        >
                          {dn.delivery_note_number || `DN #${dn.id}`}
                        </td>
                        <td>{dn.supplier?.name || "-"}</td>
                        <td>{dn.delivery_note_date || "-"}</td>
                        <td>
                          <span
                            className={`badge ${
                              dn.status === "draft"
                                ? "bg-warning text-dark"
                                : dn.status === "verified"
                                ? "bg-success"
                                : dn.status === "cancelled"
                                ? "bg-danger"
                                : "bg-secondary"
                            }`}
                          >
                            {dn.status}
                          </span>
                        </td>
                        <td>
                          {dn.total_amount
                            ? parseFloat(dn.total_amount).toFixed(2)
                            : "â€”"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-muted">{t("No delivery notes found.")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : activeTab === "deliveryNotes" ? (
        <>

          <div className="rq-card">
            <div className="rq-head">
              <div>
                <h5 className="rq-title">{t("Delivery Notes")}</h5>
                <p className="rq-subtitle">
                  {isMobile
                    ? t("Upload and review delivery notes from your phone or WhatsApp.")
                    : t("Track supplier delivery notes, verification status and total value in one place.")}
                </p>
              </div>

              <div className={`rq-controls ${isMobile ? "rq-controls--mobile" : ""}`}>
                {isMobile ? (
                  <>
                    <button
                      type="button"
                      className="rq-btn rq-btn--primary rq-btn--hero"
                      onClick={() => setIsAddDeliveryNotePopupOpen(true)}
                    >{t("+ Add Delivery Note")}</button>
                    {can("purchasing.receivings.create") && can("purchasing.delivery_notes.create") && <button type="button" className="rq-btn rq-btn--hero" onClick={() => setIsSupplierReturnOpen(true)}>{supplierReturnsText(languageId, "− New return delivery note")}</button>}
                    <button
                      type="button"
                      className="rq-btn rq-btn--action rq-btn--hero"
                      onClick={() => setShowOcrPopup(true)}
                    >{t("Delivery Note Inbox")}</button>
                    <button type="button" className="rq-btn rq-btn--action rq-btn--hero" aria-haspopup="dialog"
                      onClick={() => setPriceInboxOpen(true)}>{priceConfirmationText(languageId, "Prices to confirm")}</button>
                  </>
                ) : (
                  <div className="rq-headerActions">
                    <button
                      type="button"
                      className="rq-btn rq-btn--view"
                      onClick={() => setIsAddDeliveryNotePopupOpen(true)}
                    >{t("+ Add Direct Delivery Note")}</button>
                    {can("purchasing.receivings.create") && can("purchasing.delivery_notes.create") && <button type="button" className="rq-btn" onClick={() => setIsSupplierReturnOpen(true)}>{supplierReturnsText(languageId, "− New return delivery note")}</button>}
                    <button
                      type="button"
                      className="rq-btn rq-btn--action"
                      onClick={() => setShowOcrPopup(true)}
                    >{t("Delivery Note Inbox")}</button>
                    <button type="button" className="rq-btn rq-btn--action" aria-haspopup="dialog"
                      onClick={() => setPriceInboxOpen(true)}>{priceConfirmationText(languageId, "Prices to confirm")}</button>
                    <button
                      type="button"
                      className="rq-btn"
                      onClick={exportToExcel}
                    >{t("Export to Excel")}</button>
                  </div>
                )}

                <input aria-label={
                    isMobile
                      ? t("Search delivery notes...")
                      : t("Search by note, supplier, initiator, receiving, date or total...")
                  }
                  type="search"
                  className="rq-search"
                  placeholder={
                    isMobile
                      ? t("Search delivery notes...")
                      : t("Search by note, supplier, initiator, receiving, date or total...")
                  }
                  value={deliveryNoteSearch}
                  onChange={(event) => setDeliveryNoteSearch(event.target.value)}
                />

                {<div className="rq-statusFilters">
                    {deliveryNoteStatusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`rq-statusChip ${
                          deliveryNoteStatusFilter === option.value ? "rq-statusChip--active" : ""
                        }`}
                        aria-pressed={deliveryNoteStatusFilter === option.value}
                        onClick={() => setDeliveryNoteStatusFilter(option.value)}
                      >
                        {t(option.label)}
                        <span>{option.count}</span>
                      </button>
                    ))}
                  </div>}
                <div className="rq-statusFilters rq-documentTypeFilters" role="group" aria-label={t("Document type")}>
                  {[["all", "All types"], ["standard", "Delivery Notes"], ["rectification", "Rectifications"], ["supplier_return", "Supplier returns"]].map(([value, label]) =>
                    <button key={value} type="button" className={`rq-statusChip ${deliveryNoteTypeFilter === value ? "rq-statusChip--active" : ""}`}
                      aria-pressed={deliveryNoteTypeFilter === value} onClick={() => setDeliveryNoteTypeFilter(value)}>{t(label)}</button>)}
                </div>
              </div>
            </div>

            {!isMobile && (
              <div className="rq-stats">
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Visible / Total")}</div>
                  <div className="rq-statValue">
                    {deliveryNoteSummary.filtered} / {deliveryNoteSummary.total}
                  </div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Draft")}</div>
                  <div className="rq-statValue">{deliveryNoteSummary.draft}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Verified")}</div>
                  <div className="rq-statValue">{deliveryNoteSummary.verified}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Invoiced")}</div>
                  <div className="rq-statValue">{deliveryNoteSummary.invoiced}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Cancelled")}</div>
                  <div className="rq-statValue">{deliveryNoteSummary.cancelled}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Suppliers")}</div>
                  <div className="rq-statValue">{deliveryNoteSummary.suppliers}</div>
                </div>
                <div className="rq-stat">
                  <div className="rq-statLabel">{t("Total")}</div>
                  {Object.entries(deliveryNoteSummary.totalsByCurrency).map(([currency, total]) =>
                    <div className="rq-statValue" key={currency}>{formatCurrencyDisplay(total, currency)}</div>)}
                </div>
              </div>
            )}

            {duplicateDeliveryNoteCount > 0 && <div className="rq-errorBanner" role="alert">
              {deliveryNoteLifecycleText(languageId, "{count} delivery notes share a supplier and document number. Review them and cancel the incorrect copies.", { count: duplicateDeliveryNoteCount })}
            </div>}
            {isMobile ? (
              <div className="rq-mobileList">
                {filteredDeliveryNotes.length ? (
                  pagedRows.map((row) => (
                    <div key={row.id} className="rq-mobileCard" style={mobileTextStyle}>
                      <div className="rq-mobileTop">
                        <span className="rq-mobileCode">{row.deliveryNoteNumber}</span>
                        {row.documentType !== "standard" && <span className="rq-status rq-status--info">{t(row.documentType === "rectification" ? "Rectification" : "Supplier return")}</span>}
                            {row.correctionPending && <span className="rq-status rq-status--warning">{t(row.correctionStatus === "partial" ? "Partially rectified" : "Rectification pending")}</span>}
                            {row.isDuplicate && <span className="rq-status rq-status--danger">{deliveryNoteLifecycleText(languageId, "Duplicate delivery note")}</span>}
                        <span className={`rq-status rq-status--${row.statusTone}`}>
                          {t(row.statusLabel)}
                        </span>
                      </div>

                      <div className="rq-mobileRows">
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Supplier")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">
                            {row.supplierName}
                          </div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Date")}</div>
                          <div className="rq-mobileValue">{row.deliveryDateLabel}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Receiving")}</div>
                          <div className="rq-mobileValue">{row.receivingCode}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Initiated By")}</div>
                          <div className="rq-mobileValue rq-mobileValue--wrap">{row.initiatedByName}</div>
                        </div>
                        <div className="rq-mobileField">
                          <div className="rq-mobileLabel">{t("Total")}</div>
                          <div className="rq-mobileValue">{row.totalAmountLabel}</div>
                        </div>
                      </div>

                      <div className="rq-mobileActions">
                        {row.hasOriginalCopy && (
                          <button
                            type="button"
                            className="rq-btn rq-btn--original"
                            onClick={(event) => handleOpenDeliveryNoteOriginal(row, event)}
                            title={`${originalViewerText(languageId, "Open in separate window")}${row.sourceOcrFileName ? ` · ${row.sourceOcrFileName}` : ""}`}
                          >
                            {t("Original")}
                          </button>
                        )}
                        <button
                          type="button"
                          className="rq-btn rq-btn--primary rq-btn--hero"
                          onClick={() => handleOpenDeliveryNote(row.id)}
                        >{t("View / Edit")}</button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rq-empty">{t("No delivery notes match your search.")}</div>
                )}
              </div>
            ) : (
              <div className="rq-tableWrap">
                <table className="rq-table rq-table--deliveryNotes">
                  <thead>
                    <tr>
                      <th className="rq-col-dn-number">{t("Delivery Note")}</th>
                      <th className="rq-col-dn-status">{t("Status")}</th>
                      <th className="rq-col-dn-supplier">{t("Supplier")}</th>
                      <th className="rq-col-dn-date">{t("Date")}</th>
                      <th className="rq-col-dn-receiving">{t("Receiving")}</th>
                      <th className="rq-col-dn-initiator">{t("Initiated By")}</th>
                      <th className="rq-col-dn-total">{t("Total")}</th>
                      <th className="rq-col-dn-actions" style={{ textAlign: "right" }}>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeliveryNotes.length ? (
                      pagedRows.map((row) => (
                        <tr key={row.id}>
                          <td className="rq-col-dn-number">
                            <button
                              type="button"
                              className="rq-codeBtn"
                              onClick={() => handleOpenDeliveryNote(row.id)}
                              title={row.deliveryNoteNumber}
                            >
                              {row.deliveryNoteNumber}
                            </button>
                          </td>
                          <td className="rq-col-dn-status">
                            <span className={`rq-status rq-status--${row.statusTone}`}>
                              {t(row.statusLabel)}
                            </span>
                            {row.documentType !== "standard" && <span className="rq-status rq-status--info">{t(row.documentType === "rectification" ? "Rectification" : "Supplier return")}</span>}
                        {row.correctionPending && <span className="rq-status rq-status--warning">{t(row.correctionStatus === "partial" ? "Partially rectified" : "Rectification pending")}</span>}
                        {row.isDuplicate && <span className="rq-status rq-status--danger">{deliveryNoteLifecycleText(languageId, "Duplicate delivery note")}</span>}
                          </td>
                          <td className="rq-col-dn-supplier">
                            <span className="rq-pill" title={row.supplierName}>{row.supplierName}</span>
                          </td>
                          <td className="rq-col-dn-date">{row.deliveryDateLabel}</td>
                          <td className="rq-col-dn-receiving">{row.receivingCode}</td>
                          <td className="rq-col-dn-initiator" title={row.initiatedByName || undefined}>
                            {row.initiatedByName || <span className="rq-muted">-</span>}
                          </td>
                          <td className="rq-col-dn-total">{row.totalAmountLabel}</td>
                          <td className="rq-col-dn-actions">
                            <div className="rq-actions">
                              {row.hasOriginalCopy && (
                                <button
                                  type="button"
                                  className="rq-btn rq-btn--original"
                                  onClick={(event) => handleOpenDeliveryNoteOriginal(row, event)}
                                  title={`${originalViewerText(languageId, "Open in separate window")}${row.sourceOcrFileName ? ` · ${row.sourceOcrFileName}` : ""}`}
                                >
                                  {t("Original")}
                                </button>
                              )}
                              <button
                                type="button"
                                className="rq-btn rq-btn--view"
                                onClick={() => handleOpenDeliveryNote(row.id)}
                              >{t("View / Edit")}</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="rq-empty">{t("No delivery notes match the current filters.")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          {pagination}
</div>

          {showOcrPopup && (
            <OcrOptionsPopup
              isOpen={showOcrPopup}
              onClose={() => setShowOcrPopup(false)}
              onDeleted={fetchDeliveryNotes}
              onCreated={fetchDeliveryNotes}
              onViewDeliveryNote={(id) => {
                setShowOcrPopup(false);
                handleOpenDeliveryNote(id);
              }}
            />
          )}
        </>
      ) : null}



      {/* Popup Add Order */}
          <AddOrderPopup
            isOpen={isAddPopupOpen && canAct("purchasing.orders.create")}
            onClose={() => {
              setIsAddPopupOpen(false);
              fetchOrders(); // ðŸ”„ Refrescar tabla al cerrar, aunque sea Cancel
            }}
            onAdded={(result) => {
              if (result && Array.isArray(result.multiple)) fetchOrders();
              else if (result) setOrders((prev) => [result, ...prev]);
            }}
            onDeleted={async ({ orderCode }) => {
              setIsAddPopupOpen(false);
              await fetchOrders();
              setOrderToast(
                orderCode ? t("Order {code} deleted successfully.", { code: orderCode }) : t("Order deleted successfully.")
              );
            }}
          />


          <PurchaseRequisitionDetail isOpen={isViewRequisitionOpen} requisitionId={viewRequisition?.id} initialQuantityReviewError={requisitionReviewError}
            onProcess={(review) => convertRequisition(viewRequisition, review)}
            onClose={() => {
              setIsViewRequisitionOpen(false); setViewRequisition(null); setRequisitionReviewError(null);
              if (searchParams.has("requisition_id")) { const next = new URLSearchParams(searchParams); next.delete("requisition_id"); setSearchParams(next, { replace: true }); }
            }}
            onEdit={(requisition) => navigate(`/purchasing/orders/marketplace?business_unit_id=${businessUnitId}&requisition_id=${requisition.id}`)}
            onRepeat={repeatRequisition} onOpenOrder={(order) => { setIsViewRequisitionOpen(false); openOrder(order); }}
            onDeleted={async ({ requisitionCode }) => {
              setIsViewRequisitionOpen(false); setViewRequisition(null);
              const next = new URLSearchParams(searchParams); next.delete("requisition_id"); setSearchParams(next, { replace: true });
              await fetchRequisitions(); setRequisitionToast(t("Requisition {code} deleted successfully.", { code: requisitionCode }));
            }} />

          <RequisitionTrackingPopup
            isOpen={Boolean(trackingRequisition)}
            requisition={trackingRequisition}
            onClose={() => setTrackingRequisition(null)}
          />

          <EditOrderPopup
            isOpen={isEditOrderPopupOpen}
            orderId={editingOrderId}
            onApprovalChanged={() => { fetchOrders(); fetchApprovals(); }}
            onClose={(result) => {
              setIsEditOrderPopupOpen(false);
              setEditingOrderId(null);
              if (result?.action === "sent_to_supplier") {
                setOrderToast(t("Purchase Order {code} sent to {recipient}.", { code: result.orderCode, recipient: result.supplierEmail }));
              }
              fetchOrders(); // ðŸ”„ refrescar lista tras editar
              fetchApprovals();
            }}
          />
          {orderToast && <div className="rq-toast" role="status" aria-live="polite">{orderToast}</div>}
          {/* Popup Add Receiving */}
          <AddReceivingPopup
            isOpen={isAddReceivingPopupOpen && canAct("purchasing.receivings.create")}
            onClose={() => setIsAddReceivingPopupOpen(false)}
          />

          <EditReceivingPopup
            isOpen={isEditReceivingPopupOpen}
            receivingId={editingReceivingId}
            onClose={(result) => {
              applyReceivingConfirmationResult(result);
              setIsEditReceivingPopupOpen(false);
              setEditingReceivingId(null);
              fetchReceivings(); // ðŸ”„ refrescar tabla despuÃ©s de cerrar
              if (result?.action === "confirm_delivery_note") {
                fetchDeliveryNotes();
              }
            }}
          />

          <SupplierReturnDialog isOpen={isSupplierReturnOpen} businessUnitId={businessUnitId}
            onClose={() => setIsSupplierReturnOpen(false)} onSaved={fetchDeliveryNotes}
            onViewDeliveryNote={tabPermissions.deliveryNotes ? (id) => { setIsSupplierReturnOpen(false); handleOpenDeliveryNote(id); } : undefined} />

          <AddDeliveryNotePopup
            isOpen={isAddDeliveryNotePopupOpen}
            onClose={() => {
              setIsAddDeliveryNotePopupOpen(false);
              fetchDeliveryNotes(); // ðŸ”„ refresca lista tras guardar o cancelar
            }}
          />





          <PurchasingDocumentDialog isOpen={Boolean(requisitionNotesModal)}
            title={`${t("Notes")} · ${requisitionNotesModal?.requisitionCode || ""}`}
            showCancel={false} confirmLabel={t("Close")}
            onConfirm={() => setRequisitionNotesModal(null)} onClose={() => setRequisitionNotesModal(null)}
            message={<p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{requisitionNotesModal?.notes}</p>} />

          <PurchasingDocumentDialog isOpen={Boolean(requisitionSuppliersModal)}
            title={`${t("Suppliers")} · ${requisitionSuppliersModal?.requisitionCode || ""}`}
            showCancel={false} confirmLabel={t("Close")}
            onConfirm={() => setRequisitionSuppliersModal(null)} onClose={() => setRequisitionSuppliersModal(null)}
            message={<ul style={{ margin: 0, paddingLeft: 20, overflowWrap: "anywhere" }}>
              {(requisitionSuppliersModal?.supplierNames || []).map((name) => <li key={name}>{name}</li>)}
            </ul>} />

          <PurchasingDocumentDialog isOpen={(showCleanPopup && canAct("purchasing.requisitions.delete")) || (showCleanOrdersPopup && canAct("purchasing.orders.clean"))}
            title={t(showCleanOrdersPopup ? "Clean Purchase Orders" : "Clean Non-Converted Requisitions")}
            confirmLabel={t(cleanupReview ? "Confirm deletion" : "Review deletion")}
            cancelLabel={t(cleanupReview ? "Back" : "Cancel")} tone="warning" isBusy={cleanupBusy}
            onClose={() => {
              setCleanupError("");
              if (cleanupReview) setCleanupReview(false);
              else { setShowCleanPopup(false); setShowCleanOrdersPopup(false); }
            }}
            onConfirm={handleCleanup}
            message={<div className="rq-cleanFields">
              {cleanupReview ? <>
                <p>{t(showCleanOrdersPopup ? "Delete all {status} orders between {start} and {end}?" : "Delete all non-converted requisitions between {start} and {end}?", {
                  status: t("Draft"), start: formatDate(showCleanOrdersPopup ? cleanOrdersStart : cleanStart),
                  end: formatDate(showCleanOrdersPopup ? cleanOrdersEnd : cleanEnd),
                })}</p>
                <p>{t("This action cannot be undone.")}</p>
              </> : <>
              <p>{t("This permanently deletes matching documents. Converted requisitions and non-draft orders are not included.")}</p>
              {showCleanOrdersPopup && <label>{t("Status")}
                <select value={cleanOrdersStatus} disabled={cleanupBusy} className="form-select" onChange={(event) => setCleanOrdersStatus(event.target.value)}>
                  <option value="draft">{t("Draft")}</option>
                </select>
              </label>}
              <label>{t("Start Date")}<input type="date" className="form-control" disabled={cleanupBusy}
                value={showCleanOrdersPopup ? cleanOrdersStart : cleanStart}
                onChange={(event) => (showCleanOrdersPopup ? setCleanOrdersStart : setCleanStart)(event.target.value)} /></label>
              <label>{t("End Date")}<input type="date" className="form-control" disabled={cleanupBusy}
                min={(showCleanOrdersPopup ? cleanOrdersStart : cleanStart) || undefined}
                value={showCleanOrdersPopup ? cleanOrdersEnd : cleanEnd}
                onChange={(event) => (showCleanOrdersPopup ? setCleanOrdersEnd : setCleanEnd)(event.target.value)} /></label>
              </>}
              {cleanupError && <p className="text-danger" role="alert">{cleanupError}</p>}
            </div>} />
          {dialog}
          {approvalDecision.dialog}

          {showPriceInbox && <PriceConfirmationInbox businessUnitId={businessUnitId}
            onClose={() => setPriceInboxOpen(false)} onChanged={fetchDeliveryNotes} />}

          {/* Popup: Edit Delivery Note */}
          <EditDeliveryNotePopup
            isOpen={isEditDeliveryNoteOpen}
            deliveryNoteId={editingDeliveryNoteId}
            onClose={(result) => {
              setIsEditDeliveryNoteOpen(false);
              setEditingDeliveryNoteId(null);

              // ðŸ”„ Solo refrescar si hubo eliminaciÃ³n o verificaciÃ³n
              if (result?.deleted || result?.verified || result?.reopened) {
                console.log("[DELIVERY NOTES][REFRESH] Changes detected (deleted/verified/reopened), refreshing list...");
                fetchDeliveryNotes();
              }
            }}
          />

          <BackordersCleanupPopup
            isOpen={showBackordersPopup && canAct("purchasing.orders.update")}
            onClose={(changed) => {
              setShowBackordersPopup(false);
              if (changed) {
                fetchOrders();
              }
            }}
          />








    </div>
    </div>
  );
}
